<template>
  <!-- 
      是非題PvP遊戲多點觸控優化樣式：
      - touch-action: none        → 禁用瀏覽器預設觸控手勢（縮放、滾動），確保多點觸控正常
      - user-select: none         → 防止觸控時選取文字，避免干擾遊戲
      - -webkit-touch-callout     → 禁用 iOS 長按選單（複製、貼上等）
      - -webkit-user-select       → iOS Safari 舊版兼容性，防止文字選取
  -->
  <div class="pvptfgame-container" style="touch-action: manipulation; user-select: none; -webkit-touch-callout: none; -webkit-user-select: none;">
    <!-- 小螢幕提示遮罩 -->
    <div v-if="showSmallScreenWarning && !isWarningDismissed" class="small-screen-warning">
      <div class="warning-content">
        <svg-icon icon-class="info" class="warning-icon" />
        <p class="warning-text">{{ $t('PvPTFgame.smallScreenWarning') || '本遊戲為桌面版介面，建議於電腦平板使用，且最低螢幕寬度需求為 1200px*800px' }}</p>
        <button class="warning-close" @click="dismissWarning">
          <svg-icon icon-class="close" />
        </button>
      </div>
    </div>

    <!-- 游戏区域 -->
    <div class="game-area" ref="gameAreaRef">
     

      <div class="game-header">
        <div class="game-topic">{{ gameConfig.topic }}</div>
      </div>
      <div class="timer">{{ formatTime(timeRemaining) }}
        <button class="start-button" @click="toggleGame">
          <svg-icon :icon-class="gameStarted ? (gamePaused ? 'play' : 'pause') : 'play'" />
        </button>
      </div>

      <!-- 遊戲開始前彈窗區 -->
      <div v-if="beforeGame && (playerCount !== null || !isFromActId)" class="before-game-container">
        <!-- 正式活動模式：三欄佈局（雙人模式）或兩欄佈局（單人模式） -->
        <div v-if="isFromActId" class="actid-mode-modal" :class="{ 'dual-player-layout': !isSinglePlayerMode }">
          <!-- 左側：左側玩家資訊（僅雙人模式顯示） -->
          <div v-if="!isSinglePlayerMode" class="student-info-left">
            <h4 class="student-info-title">{{ $t('PvPgame.leftPlayer') }}</h4>

            <!-- HiTeach 名單下拉選單模式 -->
            <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
              <div class="form-group">
                <select
                  @change="handleLeftRosterSelection"
                  class="student-input student-select"
                >
                  <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                  <option
                    v-for="member in classRoster"
                    :key="`left-${member.memberName}-${member.seatID}`"
                    :value="`${member.memberName}-${member.seatID}`"
                  >
                    {{ member.seatID }} - {{ member.memberName }}
                  </option>
                </select>
                <span v-if="leftStudentInfoErrors.name" class="error-text">{{ leftStudentInfoErrors.name }}</span>
              </div>

              <!-- 顯示已選擇的資訊（唯讀） -->
              <div v-if="leftStudentInfo.name" class="selected-info">
                <div class="info-item">
                  <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                  <span class="info-value">{{ leftStudentInfo.seatNumber }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                  <span class="info-value">{{ leftStudentInfo.name }}</span>
                </div>
              </div>
            </template>

            <!-- 手動輸入模式（預設） -->
            <template v-else>
              <div class="form-group">
                <input
                  v-model="leftStudentInfo.seatNumber"
                  type="text"
                  :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                  maxlength="3"
                  class="student-input"
                />
                <span v-if="leftStudentInfoErrors.seatNumber" class="error-text">{{ leftStudentInfoErrors.seatNumber }}</span>
              </div>
              <div class="form-group">
                <input
                  v-model="leftStudentInfo.name"
                  type="text"
                  :placeholder="$t('gameActManager.studentNamePlaceholder')"
                  maxlength="20"
                  class="student-input"
                />
                <span v-if="leftStudentInfoErrors.name" class="error-text">{{ leftStudentInfoErrors.name }}</span>
              </div>
            </template>
          </div>

          <!-- 中間：遊戲資訊和播放按鈕 -->
          <div class="game-info-center">
            <div class="game-title">{{ gameConfig.topic }}</div>
            <div class="game-info">
              <div class="game-time">{{ $t('PvPTFgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPTFgame.second') }}</div>
            </div>
            <!-- 按鈕區 -->
            <div class="center-buttons">
              <button class="action-button play-button play-in-center" @click="showReadyGoCountdown()">
                <svg-icon icon-class="play" class="play-icon" />
              </button>
            </div>
          </div>

          <!-- 右側：右側玩家資訊 -->
          <div class="student-info-right">
            <h4 class="student-info-title">{{ isSinglePlayerMode ? $t('gameActManager.enterStudentInfo') : $t('PvPgame.rightPlayer') }}</h4>

            <!-- HiTeach 名單下拉選單模式 -->
            <template v-if="classRosterSource === 'HT' && classRoster.length > 0">
              <div class="form-group">
                <select
                  @change="handleRightRosterSelection"
                  class="student-input student-select"
                >
                  <option value="">{{ $t('gameActManager.selectStudent') }}</option>
                  <option
                    v-for="member in classRoster"
                    :key="`right-${member.memberName}-${member.seatID}`"
                    :value="`${member.memberName}-${member.seatID}`"
                  >
                    {{ member.seatID }} - {{ member.memberName }}
                  </option>
                </select>
                <span v-if="rightStudentInfoErrors.name" class="error-text">{{ rightStudentInfoErrors.name }}</span>
              </div>

              <!-- 顯示已選擇的資訊（唯讀） -->
              <div v-if="rightStudentInfo.name" class="selected-info">
                <div class="info-item">
                  <span class="info-label">{{ $t('gameActManager.seatNumber') }}:</span>
                  <span class="info-value">{{ rightStudentInfo.seatNumber }}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">{{ $t('gameActManager.studentName') }}:</span>
                  <span class="info-value">{{ rightStudentInfo.name }}</span>
                </div>
              </div>
            </template>

            <!-- 手動輸入模式（預設） -->
            <template v-else>
              <div class="form-group">
                <input
                  v-model="rightStudentInfo.seatNumber"
                  type="text"
                  :placeholder="$t('gameActManager.seatNumberPlaceholder')"
                  maxlength="3"
                  class="student-input"
                />
                <span v-if="rightStudentInfoErrors.seatNumber" class="error-text">{{ rightStudentInfoErrors.seatNumber }}</span>
              </div>
              <div class="form-group">
                <input
                  v-model="rightStudentInfo.name"
                  type="text"
                  :placeholder="$t('gameActManager.studentNamePlaceholder')"
                  maxlength="20"
                  class="student-input"
                />
                <span v-if="rightStudentInfoErrors.name" class="error-text">{{ rightStudentInfoErrors.name }}</span>
              </div>
            </template>
          </div>
        </div>

        <!-- Demo/WebView/預覽 模式：原有佈局 -->
        <div v-else class="before-game-modal">
          <div class="game-title">{{ gameConfig.topic }}</div>
          <div class="game-info">
            <div class="game-time">{{ $t('PvPTFgame.gameDuration') }}： {{ gameConfig.gameDuration }} {{ $t('PvPTFgame.second') }}</div>

          </div>
          <!-- 推送後顯示短網址 -->
          <div v-if="pushedShortUrl" class="url-wrap url-wrap-centered">
              <span class="url-title">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span class="short-url">{{ pushedShortCode }}</span>
              <span class="copy-btn" @click="copyPushedShortUrl">
                  <svg-icon icon-class="copy" />
              </span>
              <span class="qrcode-btn" @click="showPushedQRCode">
                  <svg-icon icon-class="qrcode-new" />
              </span>
              <span v-if="isWebViewOrIframe && !isPreviewMode" class="repush-btn" @click="repush" :title="$t('Game.repush') || '重新推送'">
                 <svg-icon icon-class="upload" class="repush-icon" />
              </span>
          </div>
          <!-- QR Code overlay -->
          <div v-if="showQRCodeOverlay" class="qrcode-overlay" @click.self="closeQRCodeOverlay">
              <div class="qrcode-modal">
                  <button class="qrcode-close-btn" @click="closeQRCodeOverlay">×</button>
                  <canvas ref="qrcodeCanvas" class="qrcode-canvas"></canvas>
                  <div style="margin-top: 10px; background-color: #fff9c4; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 10px; border-radius: 5px; white-space: nowrap; font-weight: bold;">
                      <span style="color: #333; font-size: 14px;">URL：</span><span style="color: #2B8EE0; font-size: 14px; font-weight: bold;">{{ pushedShortUrlHost }}</span><span style="color: #000; font-size: 26px; background-color: #fff; padding: 4px 6px; border-radius: 3px; font-weight: bold;">{{ pushedShortCode }}</span>
                  </div>
              </div>
          </div>

          <div class="game-buttons">
            <button class="action-button play-button" @click="showReadyGoCountdown()">
              <svg-icon icon-class="play" class="play-icon"/>
            </button>
            <!-- 預覽模式下隱藏設定和推送按鈕 -->
            <button v-if="!hasConfigUrl && !isPreviewMode" class="action-button setting-button" @click="goToSettings()">
              <svg-icon icon-class="setting-game" class="setting-icon"/>
            </button>
            <!-- 推送按鈕：在 WebView 或 iframe 環境中顯示（恢復模式下可能 isLoggedIn 尚未更新） -->
            <button v-if="isWebViewOrIframe && !isPreviewMode && (isLoggedIn || isRestoredMode) && !pushedActivityId" class="action-button push-button"
                    @click="handleSendClick()">
              <svg-icon icon-class="upload" class="sendweb-icon" />
            </button>
            <!-- ✨ 查看成績按鈕：需校園版授權，在 WebView 或 iframe 環境中顯示，且已推送活動時可用 -->
            <button v-if="isWebViewOrIframe && !isPreviewMode && pushedActivityId && schoolVers"
                    class="action-button view-scores-button"
                    @click="openScoresPanel"
                    :title="$t('HTwbActKitHome.viewScores')">
              <svg-icon icon-class="clipboard-list" class="scores-icon" />
            </button>
            <!-- 雲端管理按鈕：推送後顯示，點擊導向遊戲雲端管理  需校園版授權，-->
            <button v-if="isWebViewOrIframe && !isPreviewMode && pushedActivityId && schoolVers"
                    class="action-button cloud-manage-button"
                    @click="openCloudManagement"
                    :title="$t('HTwbActKitHome.cloudGameManagement')">
              <svg-icon icon-class="cloud-home" class="cloud-manage-icon" />
            </button>
          </div>
        </div>
      </div>


      <!-- Ready, Go! 提示 -->
      <div v-if="showReadyGo" class="ready-go-container">
        <div class="ready-go-text">{{ readyGoText }}</div>
      </div>

      <!-- 遊戲暫停彈窗 -->
      <div v-if="gameStarted && gamePaused && !gameOver" class="pause-game-container">
        <div class="pause-game-modal">
          <div class="pause-title">{{ $t('PvPTFgame.gamePaused') || '活動暫停' }}</div>
          <div class="pause-buttons">
            <button class="action-button continue-button" @click="resumeGame()">
              <svg-icon icon-class="play"  class="play-icon"/>
            </button>
            <button class="action-button restart-button" @click="resetGame()">
              <svg-icon icon-class="refresh" class="refresh-icon" />
            </button>
          </div>
        </div>
      </div>

      <!-- 單人模式：分數置中顯示 -->
      <div v-if="isSinglePlayerMode" class="single-top-right-score">{{ leftScore }}</div>

      <!-- 左侧游戏区域 -->
      <div class="game-side left-side" :class="{ 'single-player-mode': isSinglePlayerMode }">
        <!-- 雙人模式才顯示左側分數 -->
        <div v-if="!isSinglePlayerMode" class="left-score">{{ leftScore }}</div>
        <div class="game-content-wrap">
         
          
          <!-- 左側問題區域 -->
          <div class="question-wrap">
            <!-- 星空背景層 -->
            <div class="stars-background">
              <div v-for="(star, index) in leftStars" :key="'left-'+index" 
                   class="star" 
                   :style="{ 
                     left: star.x + '%', 
                     top: star.y + '%', 
                     opacity: star.opacity,
                     width: star.size + 'px',
                     height: star.size + 'px',
                     backgroundColor: star.color,
                     animationDuration: getStarAnimationDuration(star, leftRocketPower) + 's'
                   }">
              </div>
            </div>
            
            <div class="question-card" :class="{ 'answered': leftAnswered }">
              <div class="question-number">Q{{ leftQuestionIndex + 1 }}</div>
              <div class="question-text">{{ leftQuestion && leftQuestion.text ? leftQuestion.text : '' }}</div>
              <div class="question-buttons">
                <button class="answer-btn circle-btn" 
                        @click="answerQuestion(true, 'left')" 
                        @touchstart="handleTouchStart($event, true, 'left')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(leftAnswered && gameConfig.answerMode === 'sync') || leftQuestionCompleted">
                  <span><svg-icon icon-class="circle" /></span>
                </button>
                <button class="answer-btn cross-btn" 
                        @click="answerQuestion(false, 'left')" 
                        @touchstart="handleTouchStart($event, false, 'left')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(leftAnswered && gameConfig.answerMode === 'sync') || leftQuestionCompleted">
                  <span><svg-icon icon-class="close" /></span>
                </button>
              </div>
              <div class="answer-status" v-if="leftAnswered && gameConfig.answerMode === 'sync'">
                <span>{{ $t('PvPTFgame.answered') }}</span>
              </div>
            </div>
          </div>
           <!-- 左側火箭效果區域 -->
           <div class="rocket-effect-area left-rocket">
            <HTrocket :power="leftRocketPower" :showPowerLevel="false" :questionCount="gameConfig.questionList ? gameConfig.questionList.length : 1" />
          </div>
        </div>
        <!-- ... -->
      </div>

      <!-- 右侧游戏区域（單人模式隱藏） -->
      <div v-if="!isSinglePlayerMode" class="game-side right-side">
        <div class="right-score">{{ rightScore }}</div>
        <div class="game-content-wrap">
           <!-- 右側火箭效果區域 -->
           <div class="rocket-effect-area right-rocket">
            <HTrocket :power="rightRocketPower" :showPowerLevel="false"  :rocketImage="require('@/assets/HTwbActAssets/PvPTFgameObj/red_rocket.webp')" :questionCount="gameConfig.questionList ? gameConfig.questionList.length : 1"/>
          </div>
          <!-- 右側問題區域 -->
          <div class="question-wrap">
            <!-- 星空背景層 -->
            <div class="stars-background">
              <div v-for="(star, index) in rightStars" :key="'right-'+index" 
                   class="star" 
                   :style="{ 
                     left: star.x + '%', 
                     top: star.y + '%', 
                     opacity: star.opacity,
                     width: star.size + 'px',
                     height: star.size + 'px',
                     backgroundColor: star.color,
                     animationDuration: getStarAnimationDuration(star, rightRocketPower) + 's'
                   }">
              </div>
            </div>
            
            <div class="question-card" :class="{ 'answered': rightAnswered }">
              <div class="question-number">Q{{ currentQuestionIndex + 1 }}</div>
              <div class="question-text">{{ currentQuestion && currentQuestion.text ? currentQuestion.text : '' }}</div>
              <div class="question-buttons">
                <button class="answer-btn circle-btn" 
                        @click="answerQuestion(true, 'right')" 
                        @touchstart="handleTouchStart($event, true, 'right')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(rightAnswered && gameConfig.answerMode === 'sync') || rightQuestionCompleted">
                  <span><svg-icon icon-class="circle" /></span>
                </button>
                <button class="answer-btn cross-btn" 
                        @click="answerQuestion(false, 'right')" 
                        @touchstart="handleTouchStart($event, false, 'right')"
                        @touchend="handleTouchEnd($event)"
                        :disabled="(rightAnswered && gameConfig.answerMode === 'sync') || rightQuestionCompleted">
                  <span><svg-icon icon-class="close" /></span>
                </button>
              </div>
              <div class="answer-status" v-if="rightAnswered && gameConfig.answerMode === 'sync'">
                <span>{{ $t('PvPTFgame.answered') }}</span>
              </div>
            </div>
          </div>
          
         
        </div>
      </div>

      <!-- 飛行分數動畫 -->
      <transition-group name="flying-score" tag="div">
        <div
          v-for="effect in flyingScoreEffects"
          :key="effect.id"
          class="flying-score-effect"
          :style="{
            left: effect.currentX + 'px',
            top: effect.currentY + 'px',
            color: effect.isCorrect ? 'blue' : 'red',
            opacity: effect.opacity
          }"
        >
          {{ effect.text }}
        </div>
      </transition-group>

      <!-- 預覽標記（右下角） -->
      <div v-if="isPreviewMode" class="preview-badge" :class="{ 'single-left': isSinglePlayerMode }">{{ $t('gameActManager.previewMode') }}</div>

    <!-- 遊戲結束彈窗 -->
    <div v-if="gameOver" class="game-over-modal">
      <!-- 單人模式：完全比照打地鼠結構與樣式 -->
      <div v-if="isSinglePlayerMode" class="tf-single-result-container" v-show="!showAnswerModal">
        <div class="result-card-wrap">
          <div class="result-card">
            <div class="result-header">
              <div class="tf-result-title">{{ $t('PvPTFgame.gameOver') }}</div>
            </div>
            <div class="result-score">
              <div class="star-score">
                <span class="score-number">{{ leftScore }}</span>
              </div>
            </div>
            <div class="result-details">
              <div class="detail-item">
                {{ $t('PvPTFgame.correctItems') }}: {{ leftCorrectCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <div class="detail-item">
                {{ $t('PvPTFgame.errorItems') }}: {{ leftWrongCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
              <div v-if="leftSubmitResult" class="rank-info">
                <div class="detail-item">
                  {{ $t('PvPTFgame.currentRank') }}: {{ leftSubmitResult.rank }}
                </div>
                <div class="detail-item">
                  {{ $t('PvPTFgame.totalParticipants') }}: {{ leftSubmitResult.totalCount }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 雙人模式：沿用原有左右結果卡片 -->
      <div v-else class="game-result-container" v-show="!showAnswerModal">
        <div class="result-card-wrap">
          <div class="win-act" v-show="isLeftWinner">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
          <div class="result-card left-result">
            <div class="result-header">
              <div class="result-title" :class="{ 'win-title': isLeftWinner, 'tie-title': isTie, 'lose-title': !isLeftWinner && !isTie }">
                {{ isLeftWinner ? $t('PvPTFgame.win') : (isTie ? $t('PvPTFgame.tie') : '') }}
              </div>
            </div>
            <div class="result-score">
              <div class="star-score">
                <span class="score-number">{{ leftScore }}</span>
              </div>
            </div>
            <div class="result-details">
              <div class="detail-item">
                {{ $t('PvPTFgame.correctItems') }}: {{ leftCorrectCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <div class="detail-item">
                {{ $t('PvPTFgame.errorItems') }}: {{ leftWrongCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <div class="detail-item" v-if="gameConfig.answerMode === 'individual'">
                {{ $t('PvPTFgame.answerTime') }}: {{ formatAnswerTime(leftAnswerTime) }}
              </div>
              <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
              <div v-if="leftSubmitResult" class="rank-info">
                <div class="detail-item">
                  {{ $t('PvPTFgame.currentRank') }}: {{ leftSubmitResult.rank }}
                </div>
                <div class="detail-item">
                  {{ $t('PvPTFgame.totalParticipants') }}: {{ leftSubmitResult.totalCount }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="result-card-wrap" v-show="!showAnswerModal">
          <div class="win-act" v-show="isRightWinner">
            <img src="@/assets/HTwbActKitCSS/shine.png" alt="">
          </div>
          <div class="result-card right-result">
            <div class="result-header">
              <div class="result-title" :class="{ 'win-title': isRightWinner, 'tie-title': isTie, 'lose-title': !isRightWinner && !isTie }">
                {{ isRightWinner ? $t('PvPTFgame.win') : (isTie ? $t('PvPTFgame.tie') : '') }}
              </div>
            </div>
            <div class="result-score">
              <div class="star-score">
                <span class="score-number">{{ rightScore }}</span>
              </div>
            </div>
            <div class="result-details">
              <div class="detail-item">
                {{ $t('PvPTFgame.correctItems') }}: {{ rightCorrectCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <div class="detail-item">
                {{ $t('PvPTFgame.errorItems') }}: {{ rightWrongCount }} {{ $t('PvPTFgame.items') }}
              </div>
              <div class="detail-item" v-if="gameConfig.answerMode === 'individual'">
                {{ $t('PvPTFgame.answerTime') }}: {{ formatAnswerTime(rightAnswerTime) }}
              </div>
              <!-- 排名資訊（僅在成績提交後有回傳時顯示） -->
              <div v-if="rightSubmitResult" class="rank-info">
                <div class="detail-item">
                  {{ $t('PvPTFgame.currentRank') }}: {{ rightSubmitResult.rank }}
                </div>
                <div class="detail-item">
                  {{ $t('PvPTFgame.totalParticipants') }}: {{ rightSubmitResult.totalCount }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 答案彈窗 -->
      <div v-if="showAnswerModal" class="answer-modal-mask">
          <div class="answer-modal-card">
            <div class="answer-modal-header">
              <span class="answer-modal-title">{{ $t('PvPTFgame.referenceAnswers') }}</span>
              <button class="answer-modal-close" @click="toggleAnswerModal">×</button>
            </div>
            <div class="answer-modal-content-container">
              <div class="answer-modal-content">
                <div class="answer-modal-section">
                  <div class="answer-label-container">
                    <div class="answer-label correct">O</div>
                  </div>
                  <div class="answer-list">
                    <span v-for="(question, idx) in gameConfig.questionList.filter(q => q.isTrue)" :key="'c-'+idx" class="answer-item correct">{{ question.text }}</span>
                  </div>
                </div>
                <div class="answer-modal-section">
                  <div class="answer-label-container">
                    <div class="answer-label wrong">X</div>
                  </div>
                  <div class="answer-list">
                    <span v-for="(question, idx) in gameConfig.questionList.filter(q => !q.isTrue)" :key="'e-'+idx" class="answer-item wrong">{{ question.text }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      <!-- 底部按鈕區 -->
      <div class="game-result-actions" v-show="!showAnswerModal">
        <button class="action-button answer-button" @click="toggleAnswerModal">
          <span class="answer-text">{{ $t('PvPTFgame.showAnswers') }}</span>
        </button>
        <button class="action-button restart-button" @click="resetGame">
          <svg-icon icon-class="refresh" class="refresh-icon" />
        </button>
      </div>
    </div>
  </div>
    </div>

    <!-- ✨ 新增：成績查看面板 -->
    <ActivityScoresPanel
      v-if="showScoresPanel"
      :activity-id="pushedActivityId"
      :activity-name="pushedActivityName"
      :roster-source="classRosterSource || 'general'"
      @back="closeScoresPanel"
    />

    <!-- 推送確認彈窗 -->
    <PushConfirmModal
      v-model="showPushConfirm"
      :activity-name="gameConfig.topic"
      @confirm="executePush"
    />
</template>

<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, onUnmounted, computed, inject, nextTick } from 'vue';
import { Loading } from 'quasar';
import HTrocket from './HTrocket.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/components/SvgIcon.vue';
import ActivityScoresPanel from '@/GameActManager/components/ActivityScoresPanel.vue';
import PushConfirmModal from '@/GameActManager/components/PushConfirmModal.vue';
import api from '@/api';
import gameActivityAPI from '@/api/gameActivityAPI';
import mindmapAPI from '@/api/mindmapAPI';
import { validateAndRedirectGame } from '@/utils/gameActivityRouter';
import jwt_decode from 'jwt-decode';
import { useGameMessageBridge } from '@/composable/useGameMessageBridge';
import { useGameActivityPush } from '@/composable/useGameActivityPush';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';
import { useSchoolVersion } from '@/composable/useSchoolVersion';
import QRCode from 'qrcode';

const router = useRouter();
const { t, locale } = useI18n();
const iviewMessage = inject("$Message");

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTpvpTFGamePlay');

// 校園版授權判斷
const { schoolVers } = useSchoolVersion();

// 使用遊戲訊息橋接器 Composable（處理 Init.GameHome 和使用者驗證）
useGameMessageBridge('HTpvpTFGamePlay');

// 選擇器引用
const gameAreaRef = ref(null);
const leftSideRef = ref(null);
const rightSideRef = ref(null);

// 遊戲狀態
const gameStarted = ref(false);
const gamePaused = ref(false);
const gameOver = ref(false);
const timeRemaining = ref(0);
const leftScore = ref(0);
const rightScore = ref(0);
const scoreSubmitted = ref(false); // 防抖旗標：確保成績只提交一次
const leftSubmitResult = ref(null); // 左側玩家排名資訊
const rightSubmitResult = ref(null); // 右側玩家排名資訊

// 音效引用
const backgroundMusic = ref(null);
const correctSound = ref(null);
const wrongSound = ref(null);
const tieSound = ref(null);
const winSound = ref(null);
const readySound = ref(null);
const goSound = ref(null);

// 通信工具類
const MessageBridge = {
    // 環境類型
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    // 當前環境
    currentEnv: 'webview',
    // 初始化
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    // 發送消息
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            // 先發送給自己（當前 window）
            window.postMessage(data, origin);

            // 如果在 iframe 內，再同步發送給父層 window
            try {
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage(data, origin);
                }
            } catch (e) {
                // 跨域時可能會丟例外，忽略即可
            }
        }
    },
    // 添加消息監聽器
    addEventListener(callback) {
        const wrappedCallback = (event) => callback(event);
        // 將包裝後的回調存儲起來，以便後續移除
        if (!this._listeners) {
            this._listeners = new WeakMap();
        }
        this._listeners.set(callback, wrappedCallback);

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', wrappedCallback);
        } else {
            window.addEventListener('message', wrappedCallback);
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (!this._listeners) {
            return;
        }
        const wrappedCallback = this._listeners.get(callback);
        if (!wrappedCallback) {
            return;
        }

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', wrappedCallback);
        } else {
            window.removeEventListener('message', wrappedCallback);
        }
        this._listeners.delete(callback);
    }
};

// 初始化消息橋接器
const messageBridge = MessageBridge.init();

// WebView 環境檢測
const isWebView = computed(() => {
    return messageBridge.currentEnv === MessageBridge.ENV.WEBVIEW;
});

// iframe 環境檢測
const isInIframe = computed(() => {
    try {
        return window.self !== window.top;
    } catch (e) {
        return true; // 如果拋出異常，說明在跨域 iframe 中
    }
});

// WebView 或 iframe 環境（用於顯示推送與檢視成績按鈕）
const isWebViewOrIframe = computed(() => {
    return isWebView.value || isInIframe.value;
});

// 判斷是否應該使用新版 Push 功能（需校園版授權 + iframe 環境或 HiTeach 版本支援）
const shouldUseNewPushFunc = computed(() => {
    if (!schoolVers.value) return false;
    return isNewPushFuncVersion.value || isInIframe.value;
});

// 推送按鈕冷卻狀態
const isPushing = ref(false);

// ✨ 成績查看面板相關狀態
const showScoresPanel = ref(false);
const pushedActivityId = ref('');  // 儲存最新推送的活動 ID
const pushedActivityName = ref(''); // 儲存活動名稱
const pushedShortUrl = ref(''); // 儲存最新推送的完整短網址（如 https://hiteach.cc/abc123）
const showQRCodeOverlay = ref(false);
const qrcodeCanvas = ref(null);

// 從完整短網址拆出 host 與 code（如 "https://hiteach.cc/" 與 "abc123"）
const pushedShortUrlHost = computed(() => {
    if (!pushedShortUrl.value) return '';
    const match = pushedShortUrl.value.match(/^(https?:\/\/[^/]+\/)/);
    return match ? match[1] : '';
});
const pushedShortCode = computed(() => {
    if (!pushedShortUrl.value || !pushedShortUrlHost.value) return pushedShortUrl.value;
    return pushedShortUrl.value.slice(pushedShortUrlHost.value.length);
});

// 推送確認彈窗狀態
const showPushConfirm = ref(false);

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 短網址域名配置
// 短網址域名配置（已移至 useGameActivityPush composable 中）
// const shortURLHost = computed(() => {
//     return locale.value === 'zh-CN' ? 'hiteach.cn' : 'hiteach.cc';
// });

// 使用統一的遊戲活動推送 composable
const { createAndPushShortUrl, isLoggedIn } = useGameActivityPush({
    gameName: 'tf',
    gameType: 'pvptfgame',
    gameRoute: '/ht-pvp-tfgame-play',
    questionType: 'trueFalse',
    messageBridge: messageBridge,
    htVersion: htVersion,
    serializeGameConfig: (config) => {
        // TF 遊戲需要修正 gameDuration 和 questionDuration
        // gameDuration 應該是每題時間，不是總時間
        const configForAPI = {
            ...config,
            gameDuration: config.questionDuration || 10, // 每題時間
            questionDuration: config.questionDuration || 10 // 確保 questionDuration 也存在
        };
        return JSON.stringify(configForAPI);
    }
});

// 配置加載狀態
const hasConfigUrl = ref(false);
const configUrl = ref('');

// 預覽模式相關
const isPreviewMode = ref(false);
const playerCount = ref(null); // 初始值為 null，等待 API 載入

// 判斷是否為單人模式（支援預覽模式和 actId 模式）
const isSinglePlayerMode = computed(() => {
  return playerCount.value === 1;
});

// 正式活動模式相關
const isFromActId = ref(false);

// 小螢幕警告相關
const windowWidth = ref(window.innerWidth);
const showSmallScreenWarning = computed(() => windowWidth.value < 1200);
const isWarningDismissed = ref(false);

// 恢復模式相關（教師端 iframe 翻頁恢復）
const isRestoredMode = ref(false);
const restoredActivityId = ref(''); // 恢復的活動 ID

// HiTeach 名單相關
const classRosterSource = ref(''); // 名單來源
const classRoster = ref([]); // 解析後的名單陣列
const useHiTeachRoster = ref(false); // 是否使用 HiTeach 名單

// 左側玩家資訊（雙人模式）
const leftStudentInfo = reactive({
  name: '',
  seatNumber: ''
});

// 右側玩家資訊
const rightStudentInfo = reactive({
  name: '',
  seatNumber: ''
});

// 左側玩家資訊驗證錯誤訊息
const leftStudentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

// 右側玩家資訊驗證錯誤訊息
const rightStudentInfoErrors = reactive({
  name: '',
  seatNumber: ''
});

let gameConfig = reactive({
  topic: t('gameActManager.emptyState'),
  correctVocabularyList: [],
  errorVocabularyList: [],
  gameDuration: 0,
  questionDuration: 10, // 每題時間（秒）
  background: '',
  questionList: [],
  leftQuestionList: [], // 左側獨立問題列表
  answerMode: 'sync' // 作答模式: 'sync' 或 'individual'
});

// 從 blob URL 載入配置
const loadConfigFromUrl = async (url) => {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error('載入失敗');
        }
        
        const config = await response.json();
        const configData = JSON.parse(config.data.gameConfig);

        // 特殊處理 gameDuration，計算為 questionList.length * questionDuration
        if (configData.questionList && configData.questionDuration) {
            configData.gameDuration = configData.questionList.length * configData.questionDuration;
        }

        Object.assign(gameConfig, configData);
        timeRemaining.value = gameConfig.gameDuration;
        
        // 如果有問題列表，為左側創建一個打亂順序的問題列表
        if (configData.questionList && configData.questionList.length > 0) {
            gameConfig.leftQuestionList = [...configData.questionList]
                .sort(() => Math.random() - 0.5);
        }
        
        iviewMessage.success({
            content: t('PvPTFgame.configLoadSuccess'),
            duration: 2,
            closable: true
        });
    } catch (error) {
        iviewMessage.error({
            content: t('PvPTFgame.configLoadFailed'),
            duration: 3,
            closable: true
        });
        
        // 使用預設配置
        const defaultConfig = {
            "topic": "TFgame(Demo)",
            "questionList": [
              {
                "text": "The sun rises in the east.",
                "isTrue": true
              },
              {
                "text": "A cat has eight legs.",
                "isTrue": false
              },
              {
                "text": "Birds can fly in the sky.",
                "isTrue": true
              },
              {
                "text": "Fish can live on land without water.",
                "isTrue": false
              },
              {
                "text": "A banana is yellow.",
                "isTrue": true
              },
              {
                "text": "Dogs can talk like humans.",
                "isTrue": false
              },
              {
                "text": "Winter is colder than summer.",
                "isTrue": true
              },
              {
                "text": "An apple is a kind of fruit.",
                "isTrue": true
              },
              {
                "text": "The number five is bigger than the number nine.",
                "isTrue": false
              },
              {
                "text": "You use your eyes to see.",
                "isTrue": true
              }
            ],
            "questionDuration": 10,
            "answerMode": "individual"
        };
        
        defaultConfig.gameDuration = defaultConfig.questionList.length * defaultConfig.questionDuration;
        Object.assign(gameConfig, defaultConfig);
        timeRemaining.value = gameConfig.gameDuration;
        
        // 為左側創建一個打亂順序的問題列表
        gameConfig.leftQuestionList = [...defaultConfig.questionList]
            .sort(() => Math.random() - 0.5);
    }
};

// 初始化認證（actId 模式需要匿名 token）
const initializeAuth = async () => {
  try {
    const res = await api.main.getTokenUnlogin();
    const exp = jwt_decode(res.auth_token).exp;
    localStorage.setItem('auth_token', res.auth_token);
    localStorage.setItem('auth_token_expiry', exp);
  } catch (error) {
    console.error('Error fetching auth token', error);
  }
};

// 載入預覽模式參數
const loadPreviewMode = (params) => {
  console.log('🎮 [火箭升空預覽模式] 收到的完整參數:', params);
  isPreviewMode.value = true;
  playerCount.value = params.playerCount || 2;

  // 從 params.gameParameters 提取遊戲參數
  const gp = params.gameParameters || {};
  console.log('📋 [火箭升空預覽模式] gameParameters:', gp);
  console.log('📝 [火箭升空預覽模式] topic:', gp.topic);
  console.log('❓ [火箭升空預覽模式] questionList 數量:', gp.questionList?.length || 0);
  console.log('⏱️ [火箭升空預覽模式] gameDuration (每題時間):', gp.gameDuration);
  console.log('🎯 [火箭升空預覽模式] answerMode:', gp.answerMode);

  // 注意：gameDuration 在是非題中代表「每題的時間」，不是總時間
  // 計算總遊戲時間：題目數量 * 每題時間 (questionDuration)
  const questionDuration = gp.gameDuration || 10; // 每題時間（預設 10 秒）
  const questionCount = gp.questionList?.length || 10;
  const calculatedDuration = questionCount * questionDuration;

  console.log(`📊 [火箭升空預覽模式] 計算: ${questionCount} 題 × ${questionDuration} 秒/題 = ${calculatedDuration} 秒`);

  // 映射到 gameConfig 格式
  Object.assign(gameConfig, {
    topic: gp.topic || 'Preview',
    questionList: gp.questionList || [],
    questionDuration: questionDuration, // 每題時間
    gameDuration: calculatedDuration, // 總遊戲時間
    answerMode: gp.answerMode || 'sync'
  });

  // 為左側創建一個打亂順序的問題列表（單人模式也需要）
  if (gameConfig.questionList && gameConfig.questionList.length > 0) {
    gameConfig.leftQuestionList = [...gameConfig.questionList]
      .sort(() => Math.random() - 0.5);
  }

  console.log('✅ [火箭升空預覽模式] 最終 gameConfig:', gameConfig);
  timeRemaining.value = gameConfig.gameDuration;

  // 顯示預覽模式提示（使用 nextTick 確保 Message 組件已完全初始化）
  nextTick(() => {
    try {
      if (iviewMessage && typeof iviewMessage.info === 'function') {
        iviewMessage.info({
          content: t('gameActManager.previewRefreshWarning'),
          duration: 5,
          closable: true
        });
      } else {
        console.warn('[火箭升空預覽模式] iviewMessage 未正確注入，使用 console 提示');
        console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
      }
    } catch (error) {
      console.error('[火箭升空預覽模式] 顯示提示訊息失敗:', error);
      console.info(`📢 ${t('gameActManager.previewRefreshWarning')}`);
    }
  });
};

onMounted(async () => {
    // 載入 HiTeach 版本號
    loadVersion();

    // ========================================
    // 優先級 1：預覽模式檢查
    // ========================================
    const previewData = localStorage.getItem('game_preview_data');
    const previewTimestamp = localStorage.getItem('game_preview_timestamp');

    if (previewData && previewTimestamp) {
        const now = Date.now();
        const timestamp = parseInt(previewTimestamp, 10);
        const fiveMinutes = 5 * 60 * 1000;

        // 檢查是否在 5 分鐘內
        if (now - timestamp < fiveMinutes) {
            try {
                const params = JSON.parse(previewData);

                // 檢查遊戲類型是否匹配（火箭升空）
                if (params.currentGame === 'tf') {
                    loadPreviewMode(params);

                    // 🆕 延遲清除 localStorage（30秒後），避免頁面重新整理時資料遺失
                    setTimeout(() => {
                        localStorage.removeItem('game_preview_data');
                        localStorage.removeItem('game_preview_timestamp');
                        console.log('[火箭升空] 預覽資料已清除（30秒後）');
                    }, 30000); // 30秒後清除

                    return; // ✅ 跳出，不執行後續邏輯
                }
            } catch (error) {
                console.error('解析預覽參數失敗:', error);
            }
        }

        // 過期或解析失敗則清除
        localStorage.removeItem('game_preview_data');
        localStorage.removeItem('game_preview_timestamp');
    }

    // ========================================
    // 優先級 1.5：恢復模式（教師端 iframe 翻頁恢復）
    // 由 HTCC 在翻頁回本頁時，於 URL 加上 ?restored=true&activityId={id}
    // HT 桌面版自行管理翻頁，不經過此機制
    // ========================================
    const urlParams = new URLSearchParams(window.location.search);
    const restored = urlParams.get('restored');
    const restoredActId = urlParams.get('activityId');

    if (restored === 'true' && restoredActId) {
        console.log('[HTpvpTFGamePlay] [恢復模式] activityId:', restoredActId);

        // 設定恢復模式狀態
        isRestoredMode.value = true;
        restoredActivityId.value = restoredActId;

        // 設定 pushedActivityId，讓「查看成績」按鈕出現
        pushedActivityId.value = restoredActId;
        pushedActivityName.value = '已恢復的遊戲活動';

        // 嘗試從 API 載入活動資訊（用於顯示遊戲名稱等）
        Loading.show({ message: t('Game.gameLoading') });
        try {
            // 先初始化 auth_token
            await initializeAuth();

            const response = await gameActivityAPI.getGameActivity({ id: restoredActId });
            const activity = response.data;

            if (activity) {
                pushedActivityName.value = activity.activityName || '火箭升空遊戲';

                // 解析 gameContentSettings 來載入遊戲配置（用於顯示遊戲資訊）
                if (activity.gameContentSettings) {
                    try {
                        const settings = JSON.parse(activity.gameContentSettings);

                        // 是非題遊戲特殊處理：與 actId 模式一致，將每題時間換算成總遊戲時間
                        if (settings.questionList && (settings.gameDuration || settings.questionDuration)) {
                            const perQuestionDuration = settings.questionDuration || settings.gameDuration;
                            const totalDuration = settings.questionList.length * perQuestionDuration;

                            Object.assign(gameConfig, {
                                topic: settings.topic || 'TF Game',
                                questionList: settings.questionList || [],
                                questionDuration: perQuestionDuration,
                                gameDuration: totalDuration,
                                answerMode: settings.answerMode || 'sync'
                            });

                            timeRemaining.value = totalDuration;

                            // 為左側創建一個打亂順序的問題列表
                            if (settings.questionList && settings.questionList.length > 0) {
                                gameConfig.leftQuestionList = [...settings.questionList]
                                    .sort(() => Math.random() - 0.5);
                            }
                        } else {
                            // 若設定中沒有題目或時間資訊，退回原本的直接指派邏輯
                            Object.assign(gameConfig, settings);
                        }

                        gameConfig.topic = activity.activityName || gameConfig.topic || '火箭升空遊戲';

                        console.log('[HTpvpTFGamePlay] [恢復模式] 已載入遊戲配置:', gameConfig);
                    } catch (e) {
                        console.warn('[HTpvpTFGamePlay] [恢復模式] 解析 gameContentSettings 失敗:', e);
                    }
                }

                // 設定 classRosterSource（用於成績查詢）
                classRosterSource.value = activity.classRosterSource || 'general';
                // 恢復短網址顯示
                if (activity.shortUrlCode) {
                    const host = locale.value?.toLowerCase() === 'zh-cn' ? 'hiteach.cn' : 'hiteach.cc';
                    pushedShortUrl.value = `https://${host}/${activity.shortUrlCode}`;
                }
            }
        } catch (error) {
            console.warn('[HTpvpTFGamePlay] [恢復模式] 載入活動資訊失敗:', error);
            // 即使載入失敗，仍保持恢復模式，使用預設的遊戲配置
            gameConfig.topic = '火箭升空遊戲';
        } finally {
            Loading.hide();
        }

        // 恢復模式下使用 Demo 模式的 UI（顯示推送和查看成績按鈕）
        // 不執行後續的 actId 模式邏輯
        console.log('[HTpvpTFGamePlay] [恢復模式] 已啟用恢復模式 UI');
        return;
    }
    // ========== 恢復模式檢查結束 ==========

    // ========================================
    // 優先級 2：正式活動模式 (actId)
    // ========================================
    const actId = urlParams.get('actId');

    if (actId) {
        isFromActId.value = true;
        Loading.show({ message: t('Game.gameLoading') });

        // 先初始化 auth_token（actId 模式需要匿名 token）
        await initializeAuth();

        try {
            // 呼叫 API 獲取活動資料
            const response = await gameActivityAPI.getGameActivity({ id: actId });
            console.log('🎮 API Response:', response);
            const activity = response.data;
            console.log('🎯 Activity Data:', activity);

            // 檢查活動是否開放進入
            if (!activity.enableEntry) {
                iviewMessage.warning({
                    content: t('gameActManager.activityNotOpen'),
                    duration: 0,
                    closable: true
                });
                beforeGame.value = false;
                return;
            }

            // 檢查 currentGame 是否為 tf（是非題火箭升空遊戲）
            if (validateAndRedirectGame(activity, ['tf'], router, actId)) {
                return; // 已重導向，中斷後續邏輯
            }

            // 解析遊戲內容設定
            if (activity.gameContentSettings) {
                const settings = JSON.parse(activity.gameContentSettings);
                console.log('🎲 Game Content Settings:', settings);

                // 🆕 解析 HiTeach 名單資料
                classRosterSource.value = activity.classRosterSource || '';
                useHiTeachRoster.value = settings.useHiTeachRoster || false;

                if (classRosterSource.value === 'HT' && activity.classRoster) {
                    try {
                        classRoster.value = JSON.parse(activity.classRoster);
                        console.log('👥 HiTeach 名單:', classRoster.value);
                    } catch (error) {
                        console.error('❌ 解析 classRoster 失敗:', error);
                        classRoster.value = [];
                    }
                }

                // 是非題遊戲特殊處理：計算總遊戲時間
                if (settings.questionList && settings.gameDuration) {
                    // gameDuration 代表每題時間，計算總時間
                    const questionDuration = settings.gameDuration;
                    const totalDuration = settings.questionList.length * questionDuration;

                    Object.assign(gameConfig, {
                        topic: settings.topic || 'TF Game',
                        questionList: settings.questionList || [],
                        questionDuration: questionDuration,
                        gameDuration: totalDuration,
                        answerMode: settings.answerMode || 'sync'
                    });

                    timeRemaining.value = totalDuration;

                    // 為左側創建一個打亂順序的問題列表
                    if (settings.questionList && settings.questionList.length > 0) {
                        gameConfig.leftQuestionList = [...settings.questionList]
                            .sort(() => Math.random() - 0.5);
                    }
                }

                // 設定玩家數量：優先從 activity.playerCount 讀取
                const apiPlayerCount = activity.playerCount;
                if (apiPlayerCount === 1 || apiPlayerCount === 2) {
                    playerCount.value = apiPlayerCount;
                    console.log('✅ 從 activity.playerCount 設定:', playerCount.value);
                } else {
                    playerCount.value = 1; // 是非題預設單人模式
                    console.log('✅ 使用預設值:', playerCount.value);
                }
            }

            console.log('🎲 Final gameConfig:', gameConfig);
            console.log('👥 Player Count:', playerCount.value);

            iviewMessage.success({
                content: t('gameActManager.activityLoadSuccess'),
                duration: 3,
                closable: true
            });

            // 🆕 檢查 webirs_seatid 和 webirs_name 參數，自動帶入學生資訊（單雙人模式都帶入相同資訊）
            const webirsSeatId = urlParams.get('webirs_seatid');
            const webirsName = urlParams.get('webirs_name');

            if (webirsSeatId || webirsName) {
                console.log('📋 檢測到 WebIRS 參數:', { webirsSeatId, webirsName });

                // 使用 nextTick 確保 DOM 已更新
                await nextTick();

                if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
                    // HiTeach 名單模式：根據座號找到對應的學生並選中
                    const matchedMember = classRoster.value.find(
                        member => String(member.seatID) === webirsSeatId
                    );

                    if (matchedMember) {
                        // 設定左右兩側的學生資訊（單雙人模式都帶入相同資訊）
                        leftStudentInfo.name = matchedMember.memberName;
                        leftStudentInfo.seatNumber = String(matchedMember.seatID);
                        rightStudentInfo.name = matchedMember.memberName;
                        rightStudentInfo.seatNumber = String(matchedMember.seatID);

                        // 設定下拉選單的值（需要等待 DOM 渲染）
                        await nextTick();
                        const leftSelectElement = document.querySelector('.student-info-left .student-select');
                        const rightSelectElement = document.querySelector('.student-info-right .student-select');
                        const selectValue = `${matchedMember.memberName}-${matchedMember.seatID}`;

                        if (leftSelectElement) {
                            leftSelectElement.value = selectValue;
                        }
                        if (rightSelectElement) {
                            rightSelectElement.value = selectValue;
                        }

                        console.log('✅ [HT名單模式] 已自動選中學生（左右兩側）:', matchedMember);
                    } else if (webirsName) {
                        // 如果座號沒有匹配到，但有名字參數，嘗試用名字匹配
                        const matchedByName = classRoster.value.find(
                            member => member.memberName === decodeURIComponent(webirsName)
                        );

                        if (matchedByName) {
                            leftStudentInfo.name = matchedByName.memberName;
                            leftStudentInfo.seatNumber = String(matchedByName.seatID);
                            rightStudentInfo.name = matchedByName.memberName;
                            rightStudentInfo.seatNumber = String(matchedByName.seatID);

                            await nextTick();
                            const leftSelectElement = document.querySelector('.student-info-left .student-select');
                            const rightSelectElement = document.querySelector('.student-info-right .student-select');
                            const selectValue = `${matchedByName.memberName}-${matchedByName.seatID}`;

                            if (leftSelectElement) {
                                leftSelectElement.value = selectValue;
                            }
                            if (rightSelectElement) {
                                rightSelectElement.value = selectValue;
                            }

                            console.log('✅ [HT名單模式] 透過名字匹配到學生（左右兩側）:', matchedByName);
                        } else {
                            console.warn('⚠️ [HT名單模式] 未找到匹配的學生，座號:', webirsSeatId, '名字:', webirsName);
                        }
                    }
                } else {
                    // 手動輸入模式：直接帶入座號和名字（左右兩側都帶入相同資訊）
                    if (webirsSeatId) {
                        leftStudentInfo.seatNumber = webirsSeatId;
                        rightStudentInfo.seatNumber = webirsSeatId;
                    }
                    if (webirsName) {
                        const decodedName = decodeURIComponent(webirsName);
                        leftStudentInfo.name = decodedName;
                        rightStudentInfo.name = decodedName;
                    }
                    console.log('✅ [手動輸入模式] 已自動填入學生資訊（左右兩側）:', {
                        seatNumber: leftStudentInfo.seatNumber,
                        name: leftStudentInfo.name
                    });
                }
            }

            return; // 成功載入 actId 模式，跳出
        } catch (error) {
            console.error('載入活動失敗:', error);
            iviewMessage.error({
                content: t('gameActManager.loadActivityFailed'),
                duration: 3,
                closable: true
            });
            isFromActId.value = false;
            // 繼續執行後續邏輯（使用 Demo 模式）
        } finally {
            Loading.hide();
        }
    }

    // ========================================
    // 優先級 3：原本的邏輯（Blob URL 或 WebView）
    // ========================================
    // 檢查是否有 config_url 參數 - 使用原本的方式
    const fullUrl = window.location.href;
    const configUrlIndex = fullUrl.indexOf('config_url=');
    
    if (configUrlIndex !== -1) {
        // 取得 config_url= 之後的所有內容（直到字串結束）
        configUrl.value = fullUrl.substring(configUrlIndex + 'config_url='.length);
        hasConfigUrl.value = true;
        await loadConfigFromUrl(configUrl.value);
    } else {
        hasConfigUrl.value = false;

        // Demo/WebView 模式：設定預設玩家數量為單人模式
        if (playerCount.value === null) {
            playerCount.value = 2;
            console.log('✅ Demo/WebView 模式：設定 playerCount = 2');
        }

        // 🔧 改善：只有在 iframe 環境中才優先從 sessionStorage 讀取配置
        let configLoaded = false;

        if (isInIframe.value) {
            // iframe 模式：優先從 sessionStorage 讀取配置
            console.log('[iframe 模式] 優先從 sessionStorage 讀取配置');
            const savedConfig = sessionStorage.getItem('PvPTFGameConfig');

            if (savedConfig) {
                try {
                    const config = JSON.parse(savedConfig);
                    console.log('✅ [iframe] 從 sessionStorage 載入遊戲配置:', config);

                    // 特殊處理 gameDuration，計算為 questionList.length * questionDuration
                    if (config.questionList && config.questionDuration) {
                        config.gameDuration = config.questionList.length * config.questionDuration;
                    }
                    Object.assign(gameConfig, config);
                    timeRemaining.value = gameConfig.gameDuration;

                    // 如果有問題列表，為左側創建一個打亂順序的問題列表
                    if (config.questionList && config.questionList.length > 0) {
                        gameConfig.leftQuestionList = [...config.questionList]
                            .sort(() => Math.random() - 0.5);
                    }

                    configLoaded = true;

                    // 發送訊息通知 iframe 配置已載入（可選）
                    messageBridge.postMessage({
                        action: 'Init.PvPTFGame',
                        data: config
                    });
                } catch (error) {
                    console.error('❌ [iframe] 解析 sessionStorage 配置失敗:', error);
                    configLoaded = false;
                }
            }
        }

        // 如果不是 iframe 模式，或 sessionStorage 沒有配置，使用原有邏輯
        if (!configLoaded) {
            if (isInIframe.value) {
                console.log('⚠️ [iframe] sessionStorage 無配置，使用 Demo 模式');
            } else {
                console.log('[非 iframe 模式] 使用原有訊息機制');
            }

            const demoConfig = {
                "topic": "TFgame(Demo)",
                "questionList": [
                  {
                    "text": "The sun rises in the east.",
                    "isTrue": true
                  },
                  {
                    "text": "A cat has eight legs.",
                    "isTrue": false
                  },
                  {
                    "text": "Birds can fly in the sky.",
                    "isTrue": true
                  },
                  {
                    "text": "Fish can live on land without water.",
                    "isTrue": false
                  },
                  {
                    "text": "A banana is yellow.",
                    "isTrue": true
                  },
                  {
                    "text": "Dogs can talk like humans.",
                    "isTrue": false
                  },
                  {
                    "text": "Winter is colder than summer.",
                    "isTrue": true
                  },
                  {
                    "text": "An apple is a kind of fruit.",
                    "isTrue": true
                  },
                  {
                    "text": "The number five is bigger than the number nine.",
                    "isTrue": false
                  },
                  {
                    "text": "You use your eyes to see.",
                    "isTrue": true
                  }
                ],
                "questionDuration": 10,
                "answerMode": "individual"
            };

            // 特殊處理 gameDuration
            if (demoConfig.questionList && demoConfig.questionDuration) {
                demoConfig.gameDuration = demoConfig.questionList.length * demoConfig.questionDuration;
            }
            Object.assign(gameConfig, demoConfig);
            timeRemaining.value = gameConfig.gameDuration;

            // 為左側創建一個打亂順序的問題列表
            if (demoConfig.questionList && demoConfig.questionList.length > 0) {
                gameConfig.leftQuestionList = [...demoConfig.questionList]
                    .sort(() => Math.random() - 0.5);
            }

            // 發送初始化訊息
            messageBridge.postMessage({ action: 'Init.PvPTFGame', data: demoConfig });
        }

        // 保存監聽器引用以便在 onUnmounted 時移除
        // 仍然監聽訊息，以支援外部更新配置的情況
        const pvpTFGameMessageHandler = (event) => {
            const msg = event.data;
            console.log('[HTpvpTFGamePlay] 收到訊息:', msg.action, msg);

            // 監聽 Init.PvPTFGame 訊息，載入遊戲配置
            // 注意：Init.GameHome 已由 useGameMessageBridge composable 統一處理
            if (msg.action === 'Init.PvPTFGame') {
                console.log('📥 收到 Init.PvPTFGame 訊息，更新配置');

                // 特殊處理 gameDuration，計算為 questionList.length * questionDuration
                if (msg.data.questionList && msg.data.questionDuration) {
                    msg.data.gameDuration = msg.data.questionList.length * msg.data.questionDuration;
                }
                // 若新設定不含 restoredData，嘗試從 sessionStorage 保留（設定未變動時維持已推送狀態）
                if (!msg.data.restoredData) {
                    try {
                        const existingConfig = JSON.parse(sessionStorage.getItem('PvPTFGameConfig') || '{}');
                        if (existingConfig.restoredData) {
                            const existComp = JSON.stringify({ topic: existingConfig.topic, questionList: existingConfig.questionList, questionDuration: existingConfig.questionDuration, answerMode: existingConfig.answerMode, useHiTeachRoster: existingConfig.useHiTeachRoster });
                            const newComp = JSON.stringify({ topic: msg.data.topic, questionList: msg.data.questionList, questionDuration: msg.data.questionDuration, answerMode: msg.data.answerMode, useHiTeachRoster: msg.data.useHiTeachRoster });
                            if (existComp === newComp) msg.data = { ...msg.data, restoredData: existingConfig.restoredData };
                        }
                    } catch (e) { /* sessionStorage 讀取失敗，略過保留 */ }
                }
                Object.assign(gameConfig, msg.data);
                timeRemaining.value = gameConfig.gameDuration;
                // 同步 useHiTeachRoster ref（校園版預設勾選）
                if (msg.data.useHiTeachRoster !== undefined) {
                    useHiTeachRoster.value = !!msg.data.useHiTeachRoster;
                } else if (schoolVers.value) {
                    useHiTeachRoster.value = true;
                }
                // 將設定存入 sessionStorage
                sessionStorage.setItem('PvPTFGameConfig', JSON.stringify(msg.data));

                // 如果有問題列表，為左側創建一個打亂順序的問題列表
                if (msg.data.questionList && msg.data.questionList.length > 0) {
                    // 複製問題列表並打亂順序
                    gameConfig.leftQuestionList = [...msg.data.questionList]
                        .sort(() => Math.random() - 0.5);
                }
                // 若帶有 restoredData，恢復推送狀態（短網址、查看成績按鈕）
                if (msg.data.restoredData?.activityId) {
                    pushedActivityId.value = msg.data.restoredData.activityId;
                    pushedShortUrl.value = msg.data.restoredData.shortUrl || '';
                    pushedActivityName.value = gameConfig.topic || '是非遊戲';
                    console.log('[HTpvpTFGamePlay] 恢復推送狀態 activityId:', msg.data.restoredData.activityId);
                }
            }
        };
        messageBridge.addEventListener(pvpTFGameMessageHandler);

        // 儲存監聽器引用到外部變數
        messageHandlerRef = pvpTFGameMessageHandler;
    }
});


const currentQuestionIndex = ref(0);
const currentQuestion = ref({ text: '', isTrue: false });

// 左側問題索引和問題
const leftQuestionIndex = ref(0);
const leftQuestion = ref({ text: '', isTrue: false });

// 答題狀態
const leftAnswered = ref(false);
const rightAnswered = ref(false);
const leftAnswer = ref(null);
const rightAnswer = ref(null);
const waitingForNextQuestion = ref(false);

// 作答時間記錄
const leftAnswerTime = ref(0); // 左側總作答時間
const rightAnswerTime = ref(0); // 右側總作答時間
const leftCurrentQuestionTime = ref(0); // 左側當前題目作答時間
const rightCurrentQuestionTime = ref(0); // 右側當前題目作答時間

// 各自進度跳題模式相關狀態
const leftQuestionCompleted = ref(false); // 左側是否完成當前題目
const rightQuestionCompleted = ref(false); // 右側是否完成當前題目

// 遊戲結果統計
const leftCorrectCount = ref(0);
const leftWrongCount = ref(0);
const leftMaxCombo = ref(0);
const rightCorrectCount = ref(0);
const rightWrongCount = ref(0);
const rightMaxCombo = ref(0);
const leftCurrentCombo = ref(0);
const rightCurrentCombo = ref(0);

// Ready, Go! 相關狀態

// 答案彈窗狀態
const showAnswerModal = ref(false);
// 是否顯示正確答案
const showCorrectAnswer = ref(true);

// 答案彈窗切換，與 gameOver 互為相反
function toggleAnswerModal() {
showAnswerModal.value = !showAnswerModal.value;
}

const showReadyGo = ref(false);
const readyGoText = ref('');
const isCountingDown = ref(false);

// 火箭火力等級 (根據答對題數調整)
const leftRocketPower = ref(0);
const rightRocketPower = ref(0);
const maxRocketPower = 10; // 最大火力等級

// 星空背景相關變數
const leftStars = ref([]);
const rightStars = ref([]);
const starCount = 30; // 每側星星數量

// 掉落物品
const leftItems = ref([]);
const rightItems = ref([]);

// 動畫分數效果
const flyingScoreEffects = ref([]);


// 游戏循环和计时器
let gameLoop = null;
let timerInterval = null;

// 游戏区域尺寸
let gameAreaWidth = 0;
let gameAreaHeight = 0;

// 遊戲開始前彈窗
const beforeGame = ref(true);

// 格式化时间显示
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 格式化作答時間顯示
const formatAnswerTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// 勝利判定邏輯
const isLeftWinner = computed(() => {
  if (leftScore.value > rightScore.value) {
    return true;
  } else if (leftScore.value < rightScore.value) {
    return false;
  } else {
    // 分數相同時，在 individual 模式下比較作答時間
    if (gameConfig.answerMode === 'individual') {
      return leftAnswerTime.value < rightAnswerTime.value;
    }
    return false;
  }
});

const isRightWinner = computed(() => {
  if (rightScore.value > leftScore.value) {
    return true;
  } else if (rightScore.value < leftScore.value) {
    return false;
  } else {
    // 分數相同時，在 individual 模式下比較作答時間
    if (gameConfig.answerMode === 'individual') {
      return rightAnswerTime.value < leftAnswerTime.value;
    }
    return false;
  }
});

const isTie = computed(() => {
  if (leftScore.value !== rightScore.value) {
    return false;
  }
  // 分數相同時，在 sync 模式下為平局，在 individual 模式下比較作答時間
  if (gameConfig.answerMode === 'sync') {
    return true;
  } else {
    return leftAnswerTime.value === rightAnswerTime.value;
  }
});

// 切換遊戲狀態（開始/暫停/繼續）
const toggleGame = () => {
  if (!gameStarted.value) {
    if (!isCountingDown.value) {
      showReadyGoCountdown();
    }
  } else if (gamePaused.value) {
    resumeGame();
  } else {
    pauseGame();
  }
};

// 顯示 Ready, Go! 倒數效果
const showReadyGoCountdown = () => {
  // 正式活動模式：驗證學生資訊
  if (isFromActId.value) {
    // 雙人模式：驗證左右兩側玩家資訊
    if (!isSinglePlayerMode.value) {
      // 清除錯誤訊息
      leftStudentInfoErrors.name = '';
      leftStudentInfoErrors.seatNumber = '';
      rightStudentInfoErrors.name = '';
      rightStudentInfoErrors.seatNumber = '';

      let hasError = false;

      // 驗證左側玩家
      if (!leftStudentInfo.name || leftStudentInfo.name.trim() === '') {
        leftStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        hasError = true;
      }
      if (leftStudentInfo.name && leftStudentInfo.name.length > 20) {
        leftStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        hasError = true;
      }
      if (leftStudentInfo.seatNumber && !/^\d{1,3}$/.test(leftStudentInfo.seatNumber)) {
        leftStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        hasError = true;
      }

      // 驗證右側玩家
      if (!rightStudentInfo.name || rightStudentInfo.name.trim() === '') {
        rightStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        hasError = true;
      }
      if (rightStudentInfo.name && rightStudentInfo.name.length > 20) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        hasError = true;
      }
      if (rightStudentInfo.seatNumber && !/^\d{1,3}$/.test(rightStudentInfo.seatNumber)) {
        rightStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        hasError = true;
      }

      // 🆕 雙人模式且使用 HiTeach 名單時：檢查兩方座號是否相同
      if (classRosterSource.value === 'HT' && classRoster.value.length > 0) {
        if (leftStudentInfo.seatNumber.trim() === rightStudentInfo.seatNumber.trim()) {
          rightStudentInfoErrors.name = t('gameActManager.seatNumberAlreadyUsed');
          hasError = true;
        }
      }

      if (hasError) return;

      // 儲存學生資訊到 sessionStorage（用於成績提交）
      sessionStorage.setItem('leftStudentInfo', JSON.stringify(leftStudentInfo));
      sessionStorage.setItem('rightStudentInfo', JSON.stringify(rightStudentInfo));
    } else {
      // 單人模式：驗證右側玩家資訊
      rightStudentInfoErrors.name = '';
      rightStudentInfoErrors.seatNumber = '';

      let hasError = false;

      if (!rightStudentInfo.name || rightStudentInfo.name.trim() === '') {
        rightStudentInfoErrors.name = t('gameActManager.studentNameRequired');
        hasError = true;
      }
      if (rightStudentInfo.name && rightStudentInfo.name.length > 20) {
        rightStudentInfoErrors.name = t('gameActManager.studentNameTooLong');
        hasError = true;
      }
      if (rightStudentInfo.seatNumber && !/^\d{1,3}$/.test(rightStudentInfo.seatNumber)) {
        rightStudentInfoErrors.seatNumber = t('gameActManager.seatNumberInvalid');
        hasError = true;
      }

      if (hasError) return;

      // 儲存學生資訊到 sessionStorage（用於成績提交）
      sessionStorage.setItem('rightStudentInfo', JSON.stringify(rightStudentInfo));
    }
  }

// 停止並重置所有音效
if (readySound.value) {
  readySound.value.pause();
  readySound.value.currentTime = 0;
}
if (goSound.value) {
  goSound.value.pause();
  goSound.value.currentTime = 0;
}
if (backgroundMusic.value) {
  backgroundMusic.value.pause();
  backgroundMusic.value.currentTime = 0;
}
if (correctSound.value) {
  correctSound.value.pause();
  correctSound.value.currentTime = 0;
}
if (wrongSound.value) {
  wrongSound.value.pause();
  wrongSound.value.currentTime = 0;
}
if (tieSound.value) {
  tieSound.value.pause();
  tieSound.value.currentTime = 0;
}
if (winSound.value) {
  winSound.value.pause();
  winSound.value.currentTime = 0;
}



  beforeGame.value = false;
  isCountingDown.value = true;
  showReadyGo.value = true;
  readyGoText.value = 'Ready';
  
  // 在 Ready 階段就將時間重置為總題目時間
  timeRemaining.value = gameConfig.questionList.length * gameConfig.questionDuration;
  
  // 播放 Ready 音效
  if (readySound.value) {
    readySound.value.currentTime = 0;
    readySound.value.play();
  }
  
  // 2秒後顯示 Go!
  setTimeout(() => {
    readyGoText.value = 'Go!';
    
    // 播放 Go 音效
    if (goSound.value) {
      goSound.value.currentTime = 0;
      goSound.value.play();
    }
    
    // 再過1秒後開始遊戲
    setTimeout(() => {
      showReadyGo.value = false;
      isCountingDown.value = false;
      startGame();
    }, 1000);
  }, 2000);
};

// 開始遊戲
const startGame = () => {
  // 發送遊戲開始協議給 WebView
  messageBridge.postMessage({
    action: "Game.Start",
    type: "pvptf"
  });

  // 初始化遊戲區域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 重置遊戲狀態
  gameStarted.value = true;
  gamePaused.value = false;
  gameOver.value = false;
  leftScore.value = 0;
  rightScore.value = 0;
  timeRemaining.value = gameConfig.questionList.length * gameConfig.questionDuration; // 設置總時間
  leftItems.value = [];
  rightItems.value = [];
  
  // 重置統計數據
  leftCorrectCount.value = 0;
  leftWrongCount.value = 0;
  leftMaxCombo.value = 0;
  rightCorrectCount.value = 0;
  rightWrongCount.value = 0;
  rightMaxCombo.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;
  
  // 重置作答時間
  leftAnswerTime.value = 0;
  rightAnswerTime.value = 0;
  leftCurrentQuestionTime.value = gameConfig.questionDuration;
  rightCurrentQuestionTime.value = gameConfig.questionDuration;
  
  // 重置各自進度狀態
  leftQuestionCompleted.value = false;
  rightQuestionCompleted.value = false;

  // 設置第一個問題
  currentQuestionIndex.value = 0;
  leftQuestionIndex.value = 0;
  if (gameConfig.questionList && gameConfig.questionList.length > 0) {
    // 右側問題
    currentQuestion.value = gameConfig.questionList[0];
    // 左側問題
    leftQuestion.value = gameConfig.leftQuestionList[0];
    // 記錄第一題的開始時間
    currentQuestionStartTime.value = timeRemaining.value;
  }

  // 播放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.play();
  }

  // 啟動計時器（單人模式一律視為 individual）
  if (gameConfig.answerMode === 'sync' && !isSinglePlayerMode.value) {
    // sync 模式：統一計時
    timerInterval = setInterval(() => {
      if (timeRemaining.value > 0) {
        timeRemaining.value--;
        
        const questionElapsedTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
        
        if (questionElapsedTime >= gameConfig.questionDuration && timeRemaining.value > 0) {
          checkAnswers();
        }
      } else {
        if (currentQuestionIndex.value < gameConfig.questionList.length - 1) {
          goToNextQuestion();
        } else {
          endGame();
        }
      }
    }, 1000);
  } else {
    // individual 模式：各自計時（或單人模式）
    leftCurrentQuestionTime.value = gameConfig.questionDuration;
    rightCurrentQuestionTime.value = gameConfig.questionDuration;
    
    timerInterval = setInterval(() => {
      // 總計時器
      if (timeRemaining.value > 0) {
        timeRemaining.value--;
      }
      
      // 左側題目計時（如果還沒完成所有題目）
      if (!leftQuestionCompleted.value && leftCurrentQuestionTime.value > 0) {
        leftCurrentQuestionTime.value--;
      }
      
      // 右側題目計時（如果還沒完成所有題目）
      if (!rightQuestionCompleted.value && rightCurrentQuestionTime.value > 0) {
        rightCurrentQuestionTime.value--;
      }
      
      // 檢查是否完成所有題目
      if (isSinglePlayerMode.value) {
        if (leftQuestionCompleted.value) {
          endGame();
        }
      } else {
        if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
          endGame();
        }
      }
    }, 1000);
  }
};

// 暫停遊戲
const pauseGame = () => {
  if (!gameStarted.value || gameOver.value) return;

  gamePaused.value = true;

  // 暫停背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
  }

  // 清除定時器
  clearInterval(gameLoop);
  clearInterval(timerInterval);
};

// 視窗大小調整處理
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

// 關閉小螢幕警告
const dismissWarning = () => {
  isWarningDismissed.value = true;
};

// 繼續遊戲
const resumeGame = () => {
  if (!gameStarted.value || gameOver.value || !gamePaused.value) return;

  // 確保暫停狀態被正確設置為 false
  gamePaused.value = false;
  
  // 強制更新 DOM
  nextTick(() => {
    // 繼續播放背景音樂
    if (backgroundMusic.value) {
      backgroundMusic.value.play();
    }

    // 重新啟動計時器（單人模式一律視為 individual）
    if (gameConfig.answerMode === 'sync' && !isSinglePlayerMode.value) {
      timerInterval = setInterval(() => {
        if (timeRemaining.value > 0) {
          timeRemaining.value--;
          
          const questionElapsedTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
          
          if (questionElapsedTime >= gameConfig.questionDuration && timeRemaining.value > 0) {
            checkAnswers();
          }
        } else {
          if (currentQuestionIndex.value < gameConfig.questionList.length - 1) {
            goToNextQuestion();
          } else {
            endGame();
          }
        }
      }, 1000);
    } else {
      // individual 模式：各自計時（或單人模式）
      timerInterval = setInterval(() => {
        // 總計時器
        if (timeRemaining.value > 0) {
          timeRemaining.value--;
        }
        
        // 左側題目計時（如果還沒完成所有題目）
        if (!leftQuestionCompleted.value && leftCurrentQuestionTime.value > 0) {
          leftCurrentQuestionTime.value--;
        }
        
        // 右側題目計時（如果還沒完成所有題目）
        if (!rightQuestionCompleted.value && rightCurrentQuestionTime.value > 0) {
          rightCurrentQuestionTime.value--;
        }
        
        if (isSinglePlayerMode.value) {
          if (leftQuestionCompleted.value) {
            endGame();
          }
        } else {
          if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
            endGame();
          }
        }
      }, 1000);
    }
  });
};

// 结束游戏
const endGame = () => {
  gameStarted.value = false;
  gameOver.value = true;

  // 停止背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.currentTime = 0;
  }

  // 根據遊戲結果播放相應音效
  setTimeout(() => {
    if (leftScore.value === rightScore.value) {
      // 平局
      if (tieSound.value) {
        tieSound.value.currentTime = 0;
        tieSound.value.play();
      }
    } else {
      // 有人獲勝
      if (winSound.value) {
        winSound.value.currentTime = 0;
        winSound.value.play();
      }
    }
  }, 500); // 延遲半秒再播放，讓畫面先切換

  // 清除定时器
  clearInterval(gameLoop);
  clearInterval(timerInterval);

  // 提交成績（僅限正式活動模式，且只提交一次）
  if (isFromActId.value && !scoreSubmitted.value) {
    submitGameScore();
  }
};

// 提交遊戲成績到伺服器
const submitGameScore = async () => {
  // 防抖檢查：如果已經提交過，直接返回
  if (scoreSubmitted.value) {
    console.log('⚠️ 成績已經提交過，跳過重複提交');
    return;
  }

  // 設置旗標，防止重複提交
  scoreSubmitted.value = true;

  try {
    // 從 URL 取得 actId
    const urlParams = new URLSearchParams(window.location.search);
    const actId = urlParams.get('actId');

    if (!actId) {
      console.error('無法提交成績：缺少 actId');
      scoreSubmitted.value = false; // 如果失敗，重置旗標允許重試
      return;
    }

    // 雙人模式：提交兩筆成績
    if (!isSinglePlayerMode.value) {
      const leftStudentData = JSON.parse(sessionStorage.getItem('leftStudentInfo') || '{}');
      const rightStudentData = JSON.parse(sessionStorage.getItem('rightStudentInfo') || '{}');

      // 提交左側玩家成績
      if (leftStudentData.name) {
        const leftScoreData = {
          activityId: actId,
          studentName: leftStudentData.name,
          seatNumber: leftStudentData.seatNumber || '',
          score: leftScore.value,
          correctCount: leftCorrectCount.value,
          wrongCount: leftWrongCount.value
        };

        try {
          const leftResponse = await gameActivityAPI.submitScore(leftScoreData);
          console.log('✅ 左側玩家成績提交成功');
          if (leftResponse.status === 200) {
            leftSubmitResult.value = leftResponse.data;
          }
        } catch (error) {
          console.error('❌ 左側玩家成績提交失敗:', error);
        }
      }

      // 提交右側玩家成績
      if (rightStudentData.name) {
        const rightScoreData = {
          activityId: actId,
          studentName: rightStudentData.name,
          seatNumber: rightStudentData.seatNumber || '',
          score: rightScore.value,
          correctCount: rightCorrectCount.value,
          wrongCount: rightWrongCount.value
        };

        try {
          const rightResponse = await gameActivityAPI.submitScore(rightScoreData);
          console.log('✅ 右側玩家成績提交成功');
          if (rightResponse.status === 200) {
            rightSubmitResult.value = rightResponse.data;
          }
        } catch (error) {
          console.error('❌ 右側玩家成績提交失敗:', error);
        }
      }
    } else {
      // 單人模式：提交一筆成績（使用左側分數）
      const studentData = JSON.parse(sessionStorage.getItem('rightStudentInfo') || '{}');

      if (!studentData.name) {
        console.error('無法提交成績：缺少學生姓名');
        return;
      }

      const scoreData = {
        activityId: actId,
        studentName: studentData.name,
        seatNumber: studentData.seatNumber || '',
        score: leftScore.value,
        correctCount: leftCorrectCount.value,
        wrongCount: leftWrongCount.value
      };

      try {
        const response = await gameActivityAPI.submitScore(scoreData);
        console.log('✅ 成績提交成功');
        if (response.status === 200) {
          leftSubmitResult.value = response.data;
        }
        iviewMessage.success({
          content: t('gameActManager.scoreSubmitted'),
          duration: 3,
          closable: true
        });
      } catch (error) {
        console.error('❌ 成績提交失敗:', error);
        iviewMessage.error({
          content: t('gameActManager.scoreSubmitFailed'),
          duration: 3,
          closable: true
        });
      }
    }
  } catch (error) {
    console.error('提交成績時發生錯誤:', error);
  }
};

// 重置游戏
const resetGame = () => {
  // 清除所有計時器
  clearInterval(timerInterval);
  clearInterval(gameLoop);

  // 重置遊戲狀態
  gameStarted.value = false; // 確保遊戲狀態設置為未開始
  gamePaused.value = false;  // 確保暫停狀態設置為 false
  gameOver.value = false;
  beforeGame.value = true;
  timeRemaining.value = gameConfig.gameDuration;
  currentQuestionIndex.value = 0;
  waitingForNextQuestion.value = false;
  scoreSubmitted.value = false; // 重置成績提交旗標，允許下次遊戲提交
  leftSubmitResult.value = null;
  rightSubmitResult.value = null;

  // 重置分數和統計
  leftScore.value = 0;
  rightScore.value = 0;
  leftCorrectCount.value = 0;
  rightCorrectCount.value = 0;
  leftWrongCount.value = 0;
  rightWrongCount.value = 0;
  leftCurrentCombo.value = 0;
  rightCurrentCombo.value = 0;
  leftMaxCombo.value = 0;
  rightMaxCombo.value = 0;
  
  // 重置作答時間
  leftAnswerTime.value = 0;
  rightAnswerTime.value = 0;
  leftCurrentQuestionTime.value = 0;
  rightCurrentQuestionTime.value = 0;
  
  // 重置各自進度狀態
  leftQuestionCompleted.value = false;
  rightQuestionCompleted.value = false;
  
  // 重置火箭火力
  leftRocketPower.value = 0;
  rightRocketPower.value = 0;
  
  // 重置答案状态
  resetAnswerStatus();
  
  // 清理觸控狀態
  processedTouches.value.clear();
  sideLocked.value.clear();
  
  // 重置飛行分數效果
  flyingScoreEffects.length = 0;
  
  // 初始化問題列表
  if (gameConfig.questionList && gameConfig.questionList.length > 0) {
    currentQuestion.value = gameConfig.questionList[0];
    leftQuestion.value = gameConfig.leftQuestionList[0];
  }
};

// 重置答案状态
const resetAnswerStatus = () => {
  leftAnswered.value = false;
  rightAnswered.value = false;
  leftAnswer.value = null;
  rightAnswer.value = null;
  
  // 清理觸控狀態（每題重置）
  processedTouches.value.clear();
  sideLocked.value.clear();
};

// 觸控處理標記，追蹤每一邊的觸控狀態
const processedTouches = ref(new Map());
// 側邊鎖定狀態，防止同一邊同時按圈叉
const sideLocked = ref(new Set());


// 是非題遊戲觸控開始處理 - 支援多點觸控但防止同一邊重複操作
const handleTouchStart = (event, answer, side) => {
  if (!gameStarted.value || gamePaused.value || gameOver.value) return;
  
  // 檢查該側是否已經回答（僅在 sync 模式下或已完成所有題目時禁用）
  if (gameConfig.answerMode === 'sync') {
    if ((side === 'left' && leftAnswered.value) || (side === 'right' && rightAnswered.value)) {
      event.preventDefault();
      return;
    }
  } else {
    // individual 模式下，檢查是否已完成所有題目
    if ((side === 'left' && leftQuestionCompleted.value) || (side === 'right' && rightQuestionCompleted.value)) {
      event.preventDefault();
      return;
    }
  }
  
  // 檢查該側是否已被鎖定（防止同時按圈叉）
  if (sideLocked.value.has(side)) {
    event.preventDefault();
    return;
  }
  
  // 鎖定該側
  sideLocked.value.add(side);
  
  // 遍歷所有新的觸控點
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchId = touch.identifier;
    
    // 防止重複處理同一個觸控點
    if (!processedTouches.value.has(touchId)) {
      processedTouches.value.set(touchId, { answer, side });
      
      // 立即觸發答題，不等 touchend
      answerQuestion(answer, side);
      
      // 延遲清除觸控標記和側邊鎖定
      setTimeout(() => {
        processedTouches.value.delete(touchId);
        sideLocked.value.delete(side);
      }, 200); // 200ms 防止快速重複點擊
      
      break; // 只處理第一個觸控點
    }
  }
  
  // 阻止預設行為，避免觸發 click 事件
  event.preventDefault();
  event.stopPropagation();
};

// 觸控結束處理 - 清理狀態
const handleTouchEnd = (event) => {
  // 清理結束的觸控點標記
  for (let i = 0; i < event.changedTouches.length; i++) {
    const touch = event.changedTouches[i];
    const touchData = processedTouches.value.get(touch.identifier);
    if (touchData) {
      processedTouches.value.delete(touch.identifier);
      // 短暫延遲後解除側邊鎖定
      setTimeout(() => {
        sideLocked.value.delete(touchData.side);
      }, 100);
    }
  }
};

// 回答问题
const answerQuestion = (answer, side) => {
  if (!gameStarted.value || gamePaused.value || gameOver.value) return;
  
  // 檢查是否能繼續作答
  if (gameConfig.answerMode === 'sync') {
    // sync 模式：如果已經回答過，則不再處理
    if ((side === 'left' && leftAnswered.value) || (side === 'right' && rightAnswered.value)) return;
  } else {
    // individual 模式：如果已完成所有題目，則不再處理
    if ((side === 'left' && leftQuestionCompleted.value) || (side === 'right' && rightQuestionCompleted.value)) return;
  }
  
  // 記錄答案和作答時間
  if (side === 'left') {
    leftAnswered.value = true;
    leftAnswer.value = answer;
    // 記錄左側作答時間
    if (gameConfig.answerMode === 'individual' || isSinglePlayerMode.value) {
      // individual 模式：計算從題目開始到現在的時間
      const questionTime = gameConfig.questionDuration - leftCurrentQuestionTime.value;
      leftAnswerTime.value += Math.max(1, questionTime); // 至少1秒
    } else {
      // sync 模式：使用原來的計算方式
      const questionTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
      leftCurrentQuestionTime.value = questionTime;
      leftAnswerTime.value += questionTime;
    }
  } else {
    rightAnswered.value = true;
    rightAnswer.value = answer;
    // 記錄右側作答時間
    if (gameConfig.answerMode === 'individual' || isSinglePlayerMode.value) {
      // individual 模式：計算從題目開始到現在的時間
      const questionTime = gameConfig.questionDuration - rightCurrentQuestionTime.value;
      rightAnswerTime.value += Math.max(1, questionTime); // 至少1秒
    } else {
      // sync 模式：使用原來的計算方式
      const questionTime = gameConfig.questionDuration - (timeRemaining.value % gameConfig.questionDuration);
      rightCurrentQuestionTime.value = questionTime;
      rightAnswerTime.value += questionTime;
    }
  }
  
  if (gameConfig.answerMode === 'individual' || isSinglePlayerMode.value) {
    // individual 模式：立即評分並跳下一題
    if (side === 'left') {
      checkLeftAnswer();
      goToNextQuestionForSide('left');
    } else {
      checkRightAnswer();
      goToNextQuestionForSide('right');
    }
  } else {
    // sync 模式：等待兩邊都回答
    if (leftAnswered.value && rightAnswered.value && !waitingForNextQuestion.value) {
      waitingForNextQuestion.value = true;
      checkAnswers();
    }
  }
};

// individual 模式下檢查左側答案
const checkLeftAnswer = () => {
  const isLeftCorrect = leftAnswer.value === leftQuestion.value.isTrue;
  
  if (isLeftCorrect) {
    leftScore.value += 1;
    leftCorrectCount.value += 1;
    leftCurrentCombo.value += 1;
    if (leftCurrentCombo.value > leftMaxCombo.value) {
      leftMaxCombo.value = leftCurrentCombo.value;
    }
    updateRocketPower('left');
    if (correctSound.value) {
      correctSound.value.currentTime = 0;
      correctSound.value.play();
    }
    addFlyingScoreEffect('left', true);
  } else {
    leftWrongCount.value += 1;
    leftCurrentCombo.value = 0;
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0;
      wrongSound.value.play();
    }
    addFlyingScoreEffect('left', false);
  }
};

// individual 模式下檢查右側答案
const checkRightAnswer = () => {
  const isRightCorrect = rightAnswer.value === currentQuestion.value.isTrue;
  
  if (isRightCorrect) {
    rightScore.value += 1;
    rightCorrectCount.value += 1;
    rightCurrentCombo.value += 1;
    if (rightCurrentCombo.value > rightMaxCombo.value) {
      rightMaxCombo.value = rightCurrentCombo.value;
    }
    updateRocketPower('right');
    addFlyingScoreEffect('right', true);
  } else {
    rightWrongCount.value += 1;
    rightCurrentCombo.value = 0;
    addFlyingScoreEffect('right', false);
  }
};

// individual 模式下單方跳下一題
const goToNextQuestionForSide = (side) => {
  if (side === 'left') {
    leftAnswered.value = false;
    leftAnswer.value = null;
    
    // 檢查是否還有下一題
    if (leftQuestionIndex.value + 1 < gameConfig.leftQuestionList.length) {
      leftQuestionIndex.value += 1;
      leftQuestion.value = gameConfig.leftQuestionList[leftQuestionIndex.value];
      leftCurrentQuestionTime.value = gameConfig.questionDuration; // 重置左側題目計時
    } else {
      leftQuestionCompleted.value = true;
      // 單人模式：左側完成即結束
      if (isSinglePlayerMode.value) {
        endGame();
      }
    }
  } else {
    rightAnswered.value = false;
    rightAnswer.value = null;
    
    // 檢查是否還有下一題
    if (currentQuestionIndex.value + 1 < gameConfig.questionList.length) {
      currentQuestionIndex.value += 1;
      currentQuestion.value = gameConfig.questionList[currentQuestionIndex.value];
      rightCurrentQuestionTime.value = gameConfig.questionDuration; // 重置右側題目計時
    } else {
      rightQuestionCompleted.value = true;
    }
  }
  
  // 檢查完成狀態
  if (isSinglePlayerMode.value) {
    if (leftQuestionCompleted.value) {
      endGame();
    }
  } else {
    if (leftQuestionCompleted.value && rightQuestionCompleted.value) {
      endGame();
    }
  }
};

// 檢查答案並評分
const checkAnswers = () => {
  // 左側使用左側問題檢查答案，右側使用右側問題檢查答案
  const isLeftCorrect = leftAnswer.value === leftQuestion.value.isTrue;
  const isRightCorrect = rightAnswer.value === currentQuestion.value.isTrue;
  
  // 計算需要顯示的效果數量
  let effectsToShow = 0;
  let effectsCompleted = 0;
  
  // 更新左側分數和統計
  if (isLeftCorrect) {
    leftScore.value += 1;
    leftCorrectCount.value += 1;
    leftCurrentCombo.value += 1;
    if (leftCurrentCombo.value > leftMaxCombo.value) {
      leftMaxCombo.value = leftCurrentCombo.value;
    }
    // 更新左側火箭火力
    updateRocketPower('left');
    // 播放正確音效
    if (correctSound.value) {
      correctSound.value.currentTime = 0;
      correctSound.value.play();
    }
    
    // 添加左側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('left', true, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  } else {
    leftWrongCount.value += 1;
    leftCurrentCombo.value = 0;
    // 播放錯誤音效
    if (wrongSound.value) {
      wrongSound.value.currentTime = 0;
      wrongSound.value.play();
    }
    
    // 添加左側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('left', false, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  }
  
  // 更新右側分數和統計
  if (isRightCorrect) {
    rightScore.value += 1;
    rightCorrectCount.value += 1;
    rightCurrentCombo.value += 1;
    if (rightCurrentCombo.value > rightMaxCombo.value) {
      rightMaxCombo.value = rightCurrentCombo.value;
    }
    // 更新右側火箭火力
    updateRocketPower('right');
    
    // 添加右側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('right', true, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  } else {
    rightWrongCount.value += 1;
    rightCurrentCombo.value = 0;
    
    // 添加右側飛行分數效果
    effectsToShow++;
    addFlyingScoreEffect('right', false, () => {
      effectsCompleted++;
      checkAllEffectsCompleted(effectsToShow, effectsCompleted);
    });
  }
};

// 檢查所有效果是否完成
// 記錄是否已經安排了下一題的轉換
// 防止多次呼叫 goToNextQuestion 導致計時器抖動
let nextQuestionScheduled = false;

const checkAllEffectsCompleted = (total, completed) => {
  // 如果已經安排了下一題的轉換，則不再重複安排
  if (completed >= total && !nextQuestionScheduled) {
    // 標記已經安排了下一題的轉換
    nextQuestionScheduled = true;
    
    // 所有效果都已完成，進入下一題
    // 添加額外延遲，確保所有效果都已完全消失
    setTimeout(() => {
      goToNextQuestion();
      // 重置標記，為下一次做準備
      nextQuestionScheduled = false;
    }, 500); // 延遲半秒進入下一題，讓用戶有時間看到效果
  }
};

// 初始化星星
const initStars = () => {
  leftStars.value = [];
  rightStars.value = [];
  
  for (let i = 0; i < starCount; i++) {
    // 左側星星
    leftStars.value.push(createStar());
    // 右側星星
    rightStars.value.push(createStar());
  }
};

// 創建星星對象
const createStar = () => {
  return {
    x: Math.random() * 100, // 隨機水平位置
    y: Math.random() * 100, // 隨機垂直位置
    size: Math.random() * 4 + 1, // 隨機大小 1-5px
    opacity: Math.random() * 0.7 + 0.3, // 隨機透明度 0.3-1.0
    baseDuration: Math.random() * 5 + 5, // 基礎動畫持續時間 5-10秒
    color: getRandomStarColor() // 隨機星星顏色
  };
};

// 生成隨機星星顏色
const getRandomStarColor = () => {
  // 星星顏色陣列 - 主要是白色和淺藍色系
  const colors = [
    '#ffffff', // 白色
    '#f0f8ff', // 愛麗絲藍
    '#e6e6fa', // 薔衣草色
    '#f0ffff', // 天藍色
    '#fffafa', // 雪白色
    '#f8f8ff', // 幽靈白
    '#f5f5f5'  // 白煙色
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// 根據火力等級計算星星動畫持續時間
const getStarAnimationDuration = (star, power) => {
  // 基礎動畫時間
  const baseDuration = star.baseDuration;
  // 火力等級越高，動畫速度越快（持續時間越短）
  // 增強火力等級對速度的影響，使用更大的係數(1.5)和更小的最小值(0.1)
  const speedFactor = Math.max(0.1, 1 - (power / maxRocketPower) * 1.5);
  return baseDuration * speedFactor;
};

// 更新火箭火力
const updateRocketPower = (side) => {
  if (side === 'left') {
    leftRocketPower.value = leftCorrectCount.value;
  } else {
    rightRocketPower.value = rightCorrectCount.value;
  }
};

// 飛行分數效果的動畫時間（毫秒）
const FLYING_SCORE_ANIMATION_DURATION = 1200;

// 添加飛行分數效果
const addFlyingScoreEffect = (side, isCorrect, callback) => {
  const sideElement = side === 'left' ? document.querySelector('.left-side') : document.querySelector('.right-side');
  if (sideElement) {
    const rect = sideElement.getBoundingClientRect();
    const effectId = Date.now() + (side === 'left' ? 1 : 2);
    
    // 建立新的效果元素
    const effectElement = document.createElement('div');
    effectElement.className = 'flying-score-effect';
    effectElement.style.position = 'absolute';
    effectElement.style.left = `${rect.left + rect.width / 2}px`;
    effectElement.style.top = `${rect.top + rect.height / 2}px`;
    effectElement.style.transform = 'translate(-50%, -50%)';
    effectElement.style.fontSize = '2.5rem';
    effectElement.style.fontWeight = 'bold';
    effectElement.style.color = isCorrect ? '#4CAF50' : '#F44336';
    effectElement.style.zIndex = '9999';
    effectElement.style.pointerEvents = 'none';
    effectElement.style.transition = `all ${FLYING_SCORE_ANIMATION_DURATION}ms ease-out`;
    effectElement.textContent = isCorrect ? '+1' : '✘';
    
    // 將元素添加到文檔中
    document.body.appendChild(effectElement);
    
    // 強制重新計算樣式，確保過渡效果生效
    void effectElement.offsetWidth;
    
    // 設置動畫終點樣式
    effectElement.style.transform = `translate(-50%, -${rect.height}px) scale(1.5)`;
    effectElement.style.opacity = '0';
    
    // 動畫完成後移除元素並回調
    setTimeout(() => {
      if (document.body.contains(effectElement)) {
        document.body.removeChild(effectElement);
      }
      // 回調函數，通知效果已完成
      if (callback) callback();
    }, FLYING_SCORE_ANIMATION_DURATION);
  } else {
    // 如果元素不存在，仍然調用回調以避免卡住
    if (callback) callback();
  }
};

// 記錄每題的開始時間
const currentQuestionStartTime = ref(0);

// 前進到下一題
const goToNextQuestion = () => {
  // 確保所有飛行分數效果已清除
  const flyingEffects = document.querySelectorAll('.flying-score-effect');
  flyingEffects.forEach(el => {
    if (document.body.contains(el)) {
      document.body.removeChild(el);
    }
  });
  
  // 重置答題狀態
  leftAnswered.value = false;
  rightAnswered.value = false;
  leftAnswer.value = null;
  rightAnswer.value = null;
  waitingForNextQuestion.value = false;
  
  // 前進到下一題
  currentQuestionIndex.value += 1;
  leftQuestionIndex.value += 1;
  
  if (gameConfig.questionList && currentQuestionIndex.value < gameConfig.questionList.length) {
    // 更新右側問題
    currentQuestion.value = gameConfig.questionList[currentQuestionIndex.value];
    // 更新左側問題
    leftQuestion.value = gameConfig.leftQuestionList[leftQuestionIndex.value];
    
    // 重置計時器為剩餘題目的總時間
    const remainingQuestions = gameConfig.questionList.length - currentQuestionIndex.value;
    timeRemaining.value = remainingQuestions * gameConfig.questionDuration;
    // 記錄當前題目開始時間
    currentQuestionStartTime.value = timeRemaining.value;
  } else if (gameConfig.questionList) {
    // 如果已經是最後一題，則結束遊戲
    endGame();
  }
};  

// 🆕 處理左側玩家 HiTeach 名單選擇
const handleLeftRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        leftStudentInfo.name = '';
        leftStudentInfo.seatNumber = '';
        // 清除錯誤訊息
        leftStudentInfoErrors.name = '';
        leftStudentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        leftStudentInfo.name = selectedMember.memberName;
        leftStudentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        leftStudentInfoErrors.name = '';
        leftStudentInfoErrors.seatNumber = '';
        console.log('👤 左側玩家選擇學生:', selectedMember);
    }
};

// 🆕 處理右側玩家 HiTeach 名單選擇
const handleRightRosterSelection = (event) => {
    const selectedValue = event.target.value;

    if (!selectedValue) {
        // 清空選項
        rightStudentInfo.name = '';
        rightStudentInfo.seatNumber = '';
        // 清除錯誤訊息
        rightStudentInfoErrors.name = '';
        rightStudentInfoErrors.seatNumber = '';
        return;
    }

    // 從名單中找到對應的成員
    const selectedMember = classRoster.value.find(
        member => `${member.memberName}-${member.seatID}` === selectedValue
    );

    if (selectedMember) {
        rightStudentInfo.name = selectedMember.memberName;
        rightStudentInfo.seatNumber = String(selectedMember.seatID);
        // 清除錯誤訊息
        rightStudentInfoErrors.name = '';
        rightStudentInfoErrors.seatNumber = '';
        console.log('👤 右側玩家選擇學生:', selectedMember);
    }
};

// 前往設置頁面
const goToSettings = () => {
  const config = {
    topic: gameConfig.topic,
    questionList: [...(gameConfig.questionList || [])],
    questionDuration: gameConfig.questionDuration,
    answerMode: gameConfig.answerMode
  };
  if (pushedActivityId.value) {
    config.useHiTeachRoster = useHiTeachRoster.value;
    config.restoredData = { activityId: pushedActivityId.value, shortUrl: pushedShortUrl.value };
  }
  sessionStorage.setItem('PvPTFGameConfig', JSON.stringify(config));
  router.push('/ht-pvp-tfgame-setting');
};

// 已移至 useGameActivityPush composable：
// - createGameActivityFromCurrentConfig
// - generateShortUrl
// - updateActivityWithShortUrl
// - createAndPushShortUrl
// 注意：TF 遊戲的 gameDuration 修正邏輯已在 composable 調用時的 serializeGameConfig 中處理

// 推送到終端（舊版本 HiTeach）
const sendToWebIRS = () => {
    messageBridge.postMessage({action: 'SendGame.WebIRS', data: {game: 'pvpTFGame', gameConfig: JSON.stringify(gameConfig)}});
};

// 實際執行推送邏輯
const executePush = async () => {
    console.log('HiTeach 版本：', htVersion.value, '支援新 Push 功能：', isNewPushFuncVersion.value, 'iframe 環境：', isInIframe.value);

    if (isPushing.value) return;
    isPushing.value = true;

    try {
        if (shouldUseNewPushFunc.value) {
            // 新版本 HiTeach (>= 5.5.0.0002) 或 iframe 環境：創建活動 + 推送短網址
            console.log('[HTpvpTFGamePlay] 使用新版 Push 功能（創建活動 + 短網址）', isInIframe.value ? '(iframe 環境)' : '(HiTeach 版本支援)');

            // ✨ 修改：接收返回值
            const result = await createAndPushShortUrl(gameConfig, playerCount.value || 2); // PvPTF 遊戲 playerCount 使用 ref 值或預設 2

            // ✨ 新增：更新最新推送的活動 ID（覆蓋舊值）
            if (result.success && result.activityId) {
                pushedActivityId.value = result.activityId;
                pushedActivityName.value = gameConfig.topic || '是非遊戲';
                pushedShortUrl.value = result.shortUrl || '';
                // ✅ 同步更新 classRosterSource（根據 useHiTeachRoster 判斷）
                classRosterSource.value = gameConfig.useHiTeachRoster ? 'HT' : 'general';
                // 通知 HT 儲存當前遊戲設定與 restoredData（供翻頁恢復使用）
                messageBridge.postMessage({
                    action: 'Save.TFGameSetting',
                    data: {
                        ...gameConfig,
                        restoredData: {
                            activityId: result.activityId,
                            shortUrl: result.shortUrl
                        }
                    }
                });
                console.log('[HTpvpTFGamePlay] 已更新最新推送活動 ID:', result.activityId);
                console.log('[HTpvpTFGamePlay] 已更新 classRosterSource:', classRosterSource.value);
                console.log('[HTpvpTFGamePlay] ⚠️ 注意：這會覆蓋之前的 activityId');
            }
        } else {
            // 舊版本 HiTeach 或無版本資訊：推送完整 JSON（維持現有邏輯）
            console.log('[HTpvpTFGamePlay] 使用舊版 Push 功能（推送 JSON）');
            sendToWebIRS();
            iviewMessage.success({
                content: t('PvPTFgame.sendSuccess'),
                duration: 3,
                closable: true
            });
            // 注意：舊版本無法取得 activityId，查看成績按鈕不會顯示
        }
    } catch (error) {
        console.error('[HTpvpTFGamePlay] Push 失敗:', error);
        // 錯誤訊息已在 createAndPushShortUrl 中顯示
    }

    isPushing.value = false;
};

// 重新推送目前的短網址（不重新上傳）
const repush = () => {
    if (!pushedShortUrl.value || !pushedActivityId.value) return;
    const originalUrl = `${window.location.origin}/ht-pvp-tfgame-play?actId=${pushedActivityId.value}`;
    messageBridge.postMessage({
        action: 'SendGameLink.WebIRS',
        data: {
            url: originalUrl,
            shortUrl: pushedShortUrl.value,
            activityId: pushedActivityId.value,
            gameType: 'pvptfgame'
        }
    });
    iviewMessage.success({
        content: t('Game.pushSuccess') || '重新推送成功',
        duration: 2,
        closable: true
    });
};

// 處理推送按鈕點擊
const handleSendClick = () => {
    // 非校園版：跳過確認彈窗，直接執行舊版推送
    if (!schoolVers.value) {
        executePush();
        return;
    }

    showPushConfirm.value = true;
};

// ✨ 開啟成績查看面板
const openScoresPanel = () => {
    if (!pushedActivityId.value) {
        iviewMessage.warning({
            content: t('HTwbActKitHome.noActivityPushed') || '尚未推送活動，無法查看成績',
            duration: 3,
            closable: true
        });
        return;
    }
    showScoresPanel.value = true;
};

// ✨ 關閉成績查看面板
const closeScoresPanel = () => {
    showScoresPanel.value = false;
};

// 複製短網址
const copyPushedShortUrl = () => {
    if (!pushedShortUrl.value) return;
    navigator.clipboard.writeText(pushedShortUrl.value).then(() => {
        iviewMessage.success({ content: t('gameActManager.shortUrlCopied') || '已複製連結', duration: 2 });
    }).catch(() => {
        iviewMessage.error({ content: t('gameActManager.copyFailed') || '複製失敗', duration: 2 });
    });
};

// 顯示 QR Code
const showPushedQRCode = async () => {
    showQRCodeOverlay.value = true;
    await nextTick();
    if (!qrcodeCanvas.value || !pushedShortUrl.value) return;
    try {
        await QRCode.toCanvas(qrcodeCanvas.value, pushedShortUrl.value, {
            width: 280,
            margin: 2,
            color: { dark: '#000000', light: '#FFFFFF' }
        });
    } catch (error) {
        console.error('生成 QR Code 失敗:', error);
    }
};

const closeQRCodeOverlay = () => {
    showQRCodeOverlay.value = false;
};

// 開啟遊戲雲端管理頁面
const openCloudManagement = () => {
    router.push('/game-act-manager');
};

// 组件挂载时
onMounted(() => {
  // 初始化星空背景
  initStars();
  
  // 初始化游戏区域尺寸
  if (gameAreaRef.value) {
    const rect = gameAreaRef.value.getBoundingClientRect();
    gameAreaWidth = rect.width;
    gameAreaHeight = rect.height;
  }

  // 设置初始时间
  timeRemaining.value = gameConfig.gameDuration;

  // 初始化背景音樂
  backgroundMusic.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/happy-children-352377.mp3'));
  backgroundMusic.value.loop = true;
  backgroundMusic.value.volume = 0.7; // 設置音量為50%
  
  // 初始化正確和錯誤音效
  correctSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/shine-6-268910.mp3'));
  correctSound.value.volume = 0.6;
  
  wrongSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/wrong-answer-129254.mp3'));
  wrongSound.value.volume = 0.6;
  
  // 初始化平局和獲勝音效
  tieSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/game-176807.mp3'));
  tieSound.value.volume = 0.7;
  
  winSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/orchestral-win-331233.mp3'));
  winSound.value.volume = 0.7;
  
  // 初始化 Ready 和 Go 音效
  readySound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-ready.mp3'));
  readySound.value.volume = 1;
  
  goSound.value = new Audio(require('@/assets/HTwbActAssets/gameSounds/freeTTS-go.mp3'));;
  goSound.value.volume = 1;
  // 設置 Go 音效的開始時間，跳過 Ready 部分
  goSound.value.currentTime = 0; // 根據音檔的實際情況調整

  // 加入視窗大小調整監聽器
  window.addEventListener('resize', handleResize);
});

// 组件卸载时清除定时器
onUnmounted(() => {
  clearInterval(gameLoop);
  clearInterval(timerInterval);

  // 清理觸控狀態
  processedTouches.value.clear();
  sideLocked.value.clear();

  // 移除 messageBridge 監聽器
  if (messageHandlerRef) {
    messageBridge.removeEventListener(messageHandlerRef);
    messageHandlerRef = null;
  }
  
  // 停止並釋放背景音樂
  if (backgroundMusic.value) {
    backgroundMusic.value.pause();
    backgroundMusic.value.src = '';
    backgroundMusic.value = null;
  }
  
  // 釋放正確和錯誤音效
  if (correctSound.value) {
    correctSound.value.src = '';
    correctSound.value = null;
  }
  
  if (wrongSound.value) {
    wrongSound.value.src = '';
    wrongSound.value = null;
  }
  
  // 釋放平局和獲勝音效
  if (tieSound.value) {
    tieSound.value.src = '';
    tieSound.value = null;
  }
  
  if (winSound.value) {
    winSound.value.src = '';
    winSound.value = null;
  }
  
  // 釋放 Ready 和 Go 音效
  if (readySound.value) {
    readySound.value.src = '';
    readySound.value = null;
  }

  if (goSound.value) {
    goSound.value.src = '';
    goSound.value = null;
  }

  // 移除視窗大小調整監聽器
  window.removeEventListener('resize', handleResize);
});
</script>

<style lang="less" scoped>
@import '../assets/HTwbActKitCSS/HTpvpTFGame.less';

// 推送按鈕樣式
.push-button {
    width: 60px;
    height: 60px;
    background-color: #2196F3; /* 藍色 */
    border: none;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    transition: transform 0.2s, background-color 0.2s;
    
    .sendweb-icon{
        width: 30px !important;
        height: 30px !important;
        *{
            fill: white !important;
        }
    }

    &:hover:not(.disabled) {
        transform: scale(1.1);
        background-color: #1976D2;
    }
}

.push-button.disabled {
    background-color: #9e9e9e !important; /* 灰色，使用 !important 確保優先級 */
    cursor: not-allowed !important;
    
    .sendweb-icon {
        * {
            fill: #666666 !important;
        }
    }
}

/* 遊戲內容區域使用 flex 佈局 */
.game-content-wrap {
  display: flex;
  width: 100%;
  height: 100%;
}

/* 問題區域樣式 */
.question-wrap {
  flex: 1.5;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  position: relative;
  background-color: #0a1a2a; /* 深藍色背景與火箭區域一致 */
  overflow: hidden;
}

/* 星空背景層樣式 */
.stars-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

/* 星星樣式 */
.star {
  position: absolute;
  border-radius: 50%;
  animation: starFall linear infinite;
}

@keyframes starFall {
  from {
    transform: translateY(-100%);
  }
  to {
    transform: translateY(100vh);
  }
}

/* 確保問題卡片在星空之上 */
.question-card {
  position: relative;
  z-index: 1;
}

/* 火箭效果區域樣式 */
.rocket-effect-area {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #0a1a2a; /* 深藍色背景 */
  height: 100%;
  position: relative;
  overflow: hidden;
}


/* 左右火箭區域特別樣式 */
.left-rocket {
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

.right-rocket {
  border-left: 1px solid rgba(255, 255, 255, 0.1);
}

/* 單人模式樣式 */
.left-side.single-player-mode {
  width: 100% !important;

  .game-content-wrap {
    width: 100%;
  }

  .question-wrap {
    flex: 2; /* 問題區域佔更大比例 */
  }

  .rocket-effect-area {
    flex: 1; /* 火箭區域維持比例 */
  }
}

/* 單人模式分數置中 */
.center-score {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 48px;
  font-weight: bold;
  color: #FFD700;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  z-index: 100;
}

/* 單人模式分數右上角顯示（比照雙人模式 .right-score 樣式） */
.single-top-right-score {
  position: absolute;
  top: 0px;
  right: 0px;
  font-size: 2rem;
  font-weight: bold;
  color: white;
  z-index: 1;
  background-color: rgba(0, 0, 0, 0.5);
  border-top-right-radius: 10px;
  border-bottom-left-radius: 10px;
  padding: 5px 10px;
}

/* 單人模式結果卡片樣式 */
.game-result-container.single-player-result {
  justify-content: center !important;

  .result-card-wrap:first-child {
    margin-right: 0;
  }
}

.single-player-title {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* 預覽標記樣式 */
.preview-badge {
  position: absolute; /* 相對於 .game-area */
  bottom: 10px;
  right: 10px;
  padding: 8px 16px;
  background-color: rgba(255, 152, 0, 0.9);
  color: white;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 200; /* 高於內容 */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: none;
}

/* 單人模式：預覽徽章移到左下角 */
.preview-badge.single-left {
  left: 10px;
  right: auto;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

/* 小螢幕警告遮罩 */
.small-screen-warning {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.85);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  animation: slideDown 0.3s ease-out;

  .warning-content {
    display: flex;
    align-items: center;
    padding: 14px 20px;
    gap: 12px;
    max-width: 1200px;
    margin: 0 auto;

    .warning-icon {
      width: 20px;
      height: 20px;
      color: white;
      flex-shrink: 0;
    }

    .warning-text {
      flex: 1;
      color: white;
      font-size: 13px;
      line-height: 1.5;
      margin: 0;
    }

    .warning-close {
      width: 28px;
      height: 28px;
      border: none;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s;
      flex-shrink: 0;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
      }

      svg {
        width: 14px;
        height: 14px;
        color: white;
      }
    }
  }
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

</style>
